import {
  createContext,
  type Context,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode
} from 'react';
import type { Socket } from 'socket.io-client';
import {
  type ChatHistoryMeta,
  type ChatFileItem,
  type ChatMessage,
  type ChatRepository,
  type ChatState,
  type ContactSettings,
  type HistoryCursor,
  type KeyPairJwk,
  type MessageSearchResult,
  type PeerRecord,
  type RuntimeConfig,
  type SkyChatSocketClient,
  type ThemeMode,
  type UserPreferences,
  type UserProfile,
  type UserTimeZone,
  SkyChatApi,
  calculatePrivateKeyHash,
  chatReducer,
  createChatRepository,
  createSecretStorage,
  createTranslator,
  createSkyChatSocket,
  decryptArrayBufferFromGroup,
  decryptText,
  decryptTextFromGroup,
  decryptToArrayBuffer,
  displayPeerName,
  encryptArrayBuffer,
  encryptArrayBufferForGroup,
  encryptText,
  encryptTextForGroup,
  exportPrivateKeyBackup,
  generateKeypair,
  importPrivateKeyBackup,
  initialChatState,
  keypairFromPrivate,
  loadRuntimeConfig,
  migrateLegacyKeypair,
  normalizeBaseUrl,
  normalizeMessageFileInfo,
  peerIdForMessage,
  sha256Hex,
  sortMessages,
  type KeyValueStorage
} from '@skychat/shared-core';
import {
  deleteChatBackground,
  getChatBackground,
  saveChatBackground,
  type StoredChatBackground
} from '../utils/appearance';
import { profileTimeZonePatch } from '../utils/profileTimeZone';
import {
  bindServiceWorkerBridge,
  disableWebPush,
  enableWebPush,
  getWebPushState,
  restoreWebPush,
  syncServiceWorkerState,
  type WebPushState
} from '../utils/webPush';
import { chatMessageToFileItem } from '../utils/chatFiles';
import {
  deletePersistentMedia,
  getPersistentMedia,
  savePersistentMedia
} from '../utils/mediaCache';
import {
  createChatP2PFileManager,
  isChatP2PFileMessage,
  shouldUseChatP2PFile
} from '../utils/chatP2PFiles';
import {
  clearMobileReliabilityState,
  listPendingUploads,
  listPersistedSocketOutbox,
  persistPendingUpload,
  persistSocketOutboxEntry,
  removePendingUpload,
  removePersistedSocketOutboxEntry,
  restorePendingUpload
} from '../infrastructure/storage/reliabilityStore';
import { MobileConnectionCoordinator } from '../infrastructure/realtime/connectionCoordinator';
import { requestMessageSyncV2, requestMessageSyncV2Manifest } from '../features/chat/services/messageSyncV2Service';
import { resolveIncomingTypingEntry } from '../features/chat/model/typingPresentation';
import { installMobileLifecycleCoordinator } from '../infrastructure/lifecycle/lifecycleCoordinator';
import {
  attachSeparatedGameApi,
  buildFallbackGameServiceConfig,
  connectSeparatedGameSocket,
  type GameServiceConfig,
  loadGameServiceConfig,
  runtimeApiBaseUrl
} from '../features/games/services/gameService';
import {
  getOrCreateMobileDeviceSessionId,
  MOBILE_DEVICE_KIND,
  postMobileDeviceConnection,
  postMobileDeviceDisconnection
} from '../features/devices/services/devicePresenceService';
import {
  AuthSessionExpiredError,
  chatMessageMeta,
  clearLegacyAuthStorage,
  createProfileUpdateSystemMessage,
  encryptedDirectOutbound,
  isAcceptedConversationPeer,
  isAuthFailureResult,
  isChatP2PSuccessMessage,
  maxMessageSeq,
  maxMessageTs,
  mergeChatMessages,
  mergeHydratedMessageLists,
  mergeMessageLists,
  mergeReadBy,
  messageCountsForSyncCursor,
  messageNeedsHydration,
  normalizeChatMessageForClient,
  normalizeChatP2PMessageForCache,
  normalizeDeliveryStatus,
  normalizeReadBy,
  profileFromResult,
  safeReadStatus,
  syncedMessagesFromResult,
  type DeliveryStatus
} from '../features/chat/model/chatModel';
import {
  hasPresencePayload,
  normalizeBotList,
  normalizeConfiguredPresence,
  presencePatchFromSocketPayload,
  readPreferredPresence,
  socketPresenceRevision,
  writePreferredPresence,
  type BotDefinition
} from '../features/presence/model/presenceModel';
import { sendMobileFriendRequest } from '../features/friends/services/friendRequestService';
import {
  ACCENT_PRESETS,
  DEFAULT_PREFERENCES,
  LOCALE_RESOURCES,
  accentRgb,
  accentSecondary,
  browserLocale,
  mobileTranslation,
  normalizeAccentColor,
  normalizePreferences,
  type MobilePreferences
} from './models/preferencesModel';
import { PREVIEW_ME, PREVIEW_MESSAGES, PREVIEW_PEERS } from './preview/previewData';
export type { BotCommandDefinition, BotDefinition } from '../features/presence/model/presenceModel';

export interface TypingPeerEntry {
  userId: string;
  typing_animation?: string;
  custom_typing_animation_url?: string;
}

export interface CustomStatusHistoryItem {
  id: number | string;
  icon: string;
  text: string;
  createdAt?: number;
  color_hex?: string | null;
  auto_reply_text?: string;
  auto_reply_enabled?: boolean;
  schedule_mode?: 'none' | 'weekly' | 'countdown' | string;
  schedule_days?: number[];
  schedule_start_local?: string | null;
  schedule_end_local?: string | null;
  countdown_minutes?: number | null;
  timezone?: string;
}

interface MediaResolveOptions {
  forceRefresh?: boolean;
}

interface MediaUrlCacheEntry {
  url: string;
  signature: string;
}

interface LocalSearchIndexEntry {
  messages: ChatMessage[];
  promise?: Promise<ChatMessage[]>;
}

interface ConversationSyncOptions {
  reason?: string;
  force?: boolean;
}

interface MobileMessageSyncV2State {
  cursor: number;
  bootstrapComplete: boolean;
  peerScopeIds: string[];
  observedInsertSeqs: number[];
}

type ConversationSyncFn = (options?: ConversationSyncOptions) => Promise<void>;
type SocketEventHandler = (...args: unknown[]) => void;

interface SkyChatContextValue {
  ready: boolean;
  initialUiReady: boolean;
  cacheReady: boolean;
  accountReady: boolean;
  accountRefreshing: boolean;
  accountRefreshError: string | null;
  previewMode: boolean;
  token: string | null;
  me: UserProfile | null;
  peers: PeerRecord[];
  chat: ChatState;
  bots: BotDefinition[];
  typingByPeer: Record<string, TypingPeerEntry[]>;
  gameEventVersion: number;
  chatBackground: StoredChatBackground | null;
  config: RuntimeConfig;
  preferences: MobilePreferences;
  webPushState: WebPushState;
  t(key: string, fallback?: string): string;
  api: SkyChatApi;
  login(id: string, password: string): Promise<void>;
  register(payload: { nickname: string; password: string; inviteCode: string }): Promise<void>;
  logout(): Promise<void>;
  refreshPeers(): Promise<void>;
  openPeer(peerId: string): Promise<void>;
  setPeerViewActive(peerId: string, active: boolean): void;
  loadOlderPeerHistory(peerId: string): Promise<void>;
  searchLocalMessages(peerId: string, query?: string, date?: string): Promise<MessageSearchResult[]>;
  revealLocalMessage(peerId: string, messageId: string): Promise<void>;
  listLocalChatFiles(peerId: string): Promise<ChatFileItem[]>;
  sendText(peerId: string, text: string, replyTo?: ChatMessage | null): Promise<void>;
  sendGif(peerId: string, url: string): Promise<void>;
  invokeBot(peerId: string, bot: BotDefinition, command: string, args: string[], rawText: string): Promise<void>;
  sendFile(peerId: string, file: File): Promise<void>;
  retryMessage(peerId: string, message: ChatMessage): Promise<void>;
  cancelChatP2PFile(message: ChatMessage): Promise<boolean>;
  editMessage(peerId: string, messageId: string, text: string): Promise<void>;
  deleteMessage(peerId: string, messageId: string): Promise<void>;
  clearPeer(peerId: string): Promise<void>;
  sendFriendRequest(toId: string): Promise<void>;
  respondFriendRequest(fromId: string, accept: boolean): Promise<void>;
  markRead(peerId: string): void;
  markUnread(peerId: string): void;
  updateContactSettings(peerId: string, patch: Partial<ContactSettings>): Promise<ContactSettings | null>;
  emitTyping(peerId: string, on: boolean): void;
  waitForSocketReady(timeoutMs?: number): Promise<SkyChatSocketClient>;
  emitSocketEvent(event: string, payload?: unknown, ack?: (...args: unknown[]) => void): void;
  onSocketEvent(event: string, handler: SocketEventHandler): () => void;
  updateWeatherCity(city: string): Promise<void>;
  updateWeatherVisibility(enabled: boolean): Promise<void>;
  updatePreferences(patch: Partial<UserPreferences> & Partial<MobilePreferences> & Record<string, unknown>): Promise<void>;
  enableWebPushNotifications(): Promise<void>;
  disableWebPushNotifications(): Promise<void>;
  refreshWebPushState(): Promise<void>;
  updateProfile(payload: Record<string, unknown>): Promise<void>;
  updateProfileTimeZone(timeZoneId: string, displayName: string): Promise<void>;
  uploadTypingAnimation(file: File): Promise<string>;
  setChatBackground(background: StoredChatBackground | null): Promise<void>;
  getCustomStatusHistory(): Promise<CustomStatusHistoryItem[]>;
  deleteCustomStatusHistory(id: number | string): Promise<void>;
  updateServerUrl(url: string): Promise<void>;
  exportKeyBackup(passphrase: string): Promise<Blob>;
  importKeyBackup(file: File, passphrase: string): Promise<void>;
  restoreKeyBackup(id: string, password: string): Promise<void>;
  getDecryptedFileUrl(message: ChatMessage, peerId: string, options?: MediaResolveOptions): Promise<string>;
  assetUrl(path: string): string;
}

type SkyChatContextGlobal = typeof globalThis & {
  __skychatMobileContext?: Context<SkyChatContextValue | null>;
};

const skyChatContextGlobal = globalThis as SkyChatContextGlobal;
const SkyChatContext = skyChatContextGlobal.__skychatMobileContext
  || (skyChatContextGlobal.__skychatMobileContext = createContext<SkyChatContextValue | null>(null));

const DEFAULT_WEB_PUSH_STATE: WebPushState = {
  supported: false,
  permission: 'unsupported',
  subscriptionActive: false
};

const MESSAGE_PAGE_SIZE = 30;
const CACHE_BOOT_HYDRATE_LIMIT = 12;
const BACKGROUND_CACHE_HYDRATE_LIMIT = 40;
const MESSAGE_HYDRATION_BATCH_SIZE = 6;
const FULL_SYNC_CONCURRENCY = 2;
const FULL_SYNC_THROTTLE_MS = 8000;
const MESSAGE_SYNC_V2_PAGE_SIZE = 200;
const MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS = 4096;
const MESSAGE_SYNC_V2_MIN_INTERVAL_MS = 3000;
const FOREGROUND_SYNC_DELAY_MS = 350;
const TYPING_LEASE_MS = 3000;
const TYPING_CHECK_FLOOR_MS = 80;
const ME_CACHE_KEY = 'meProfile';
const PEERS_CACHE_KEY = 'peerProfiles';

interface TypingSessionEntry {
  entry: TypingPeerEntry;
  lastTypingAt: number;
  timerId?: number;
}

function sameTypingEntry(a: TypingPeerEntry | undefined, b: TypingPeerEntry | undefined) {
  return !!a && !!b &&
    a.userId === b.userId &&
    (a.typing_animation || '') === (b.typing_animation || '') &&
    (a.custom_typing_animation_url || '') === (b.custom_typing_animation_url || '');
}

function sameTypingList(a: TypingPeerEntry[], b: TypingPeerEntry[]) {
  if (a.length !== b.length) return false;
  return a.every((item, index) => sameTypingEntry(item, b[index]));
}

function defaultHistoryMeta(): ChatHistoryMeta {
  return {
    hasMoreOlder: false,
    loadingOlder: false,
    initialLoaded: false,
    initialLoading: false,
    initialError: null,
    oldestCursor: null
  };
}

function historyCursorFromMessages(messages: ChatMessage[]): HistoryCursor | null {
  const oldest = messages[0];
  if (!oldest) return null;
  return {
    beforeTs: Number(oldest.ts || 0),
    ...(Number.isFinite(Number(oldest.seq)) ? { beforeSeq: Number(oldest.seq) } : {})
  };
}

function encryptedData(payload: unknown): string {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return String((payload as { data?: unknown }).data || '');
  }
  return String(payload || '');
}

function legacyToken(): string | null {
  try {
    return localStorage.getItem('skychat_token') || sessionStorage.getItem('skychat_token');
  } catch {
    return null;
  }
}

function peerName(peer?: PeerRecord | null): string {
  return displayPeerName(peer, '');
}

function isGroup(peer?: PeerRecord | null): boolean {
  return peer?.type === 'group';
}

function peerMetadataValue(peer: PeerRecord) {
  return peer as Record<string, unknown>;
}

function peerLastMessageTime(peer: PeerRecord, chatMessages: Array<{ ts?: number }>) {
  const localTime = chatMessages[chatMessages.length - 1]?.ts || 0;
  const remoteTime = Number(peerMetadataValue(peer).lastMessageTime || 0) || 0;
  return Math.max(localTime, remoteTime);
}

function normalizeWeatherPatch(weather: unknown): Record<string, unknown> | null {
  if (!weather || typeof weather !== 'object') return null;
  return weather as Record<string, unknown>;
}

function mergePeers(friends: PeerRecord[], groups: PeerRecord[]): PeerRecord[] {
  const map = new Map<string, PeerRecord>();
  [...friends, ...groups].forEach((peer) => map.set(peer.id, peer));
  return [...map.values()].sort((a, b) => peerName(a).localeCompare(peerName(b)));
}

type PendingSocketSend = {
  peerId: string;
  message: ChatMessage;
  inFlightSessionKey?: string;
  authRetryCount: number;
  attemptCount: number;
  createdAt: number;
  lastAttemptAt?: number;
  lastAuthErrorAttempt?: number;
};

type RuntimePresencePatch = {
  patch: Partial<UserProfile>;
  receivedAt: number;
  revision: number;
};

const MESSAGE_ACK_TIMEOUT_MS = 9000;
const MAX_MESSAGE_SEND_ATTEMPTS = 4;
const MAX_SOCKET_AUTH_RETRIES = 5;
const UPLOAD_MAX_ATTEMPTS = 4;
const UPLOAD_RETRY_BASE_DELAY_MS = 1200;
const PRESENCE_PATCH_MAX_AGE_MS = 120000;

class MobileUploadError extends Error {
  retryable: boolean;

  constructor(message: string, retryable = true) {
    super(message);
    this.name = 'MobileUploadError';
    this.retryable = retryable;
  }
}


function mediaCacheSlot(message: ChatMessage, peerId: string): string {
  const value = message as ChatMessage & { file?: { id?: string; url?: string; name?: string } };
  return String(message.id || value.file?.id || value.file?.url || value.file?.name || `${peerId}:${message.ts || 'file'}`);
}

function mediaCacheSignature(message: ChatMessage, peerId: string, ownerId = ''): string {
  return JSON.stringify([
    ownerId,
    peerId,
    message.id || '',
    message.file?.id || '',
    message.file?.url || '',
    message.file?.mime || '',
    message.file?.type || '',
    message.iv || '',
    message.ts || 0
  ]);
}

function bytesMatch(bytes: Uint8Array, signature: number[], offset = 0): boolean {
  if (bytes.length < signature.length + offset) return false;
  return signature.every((value, index) => bytes[index + offset] === value);
}

function imageBufferLooksValid(buffer: ArrayBuffer, mime: string): boolean {
  const normalized = String(mime || '').split(';')[0].trim().toLowerCase();
  if (!normalized.startsWith('image/')) return true;
  if (buffer.byteLength <= 0) return false;

  const bytes = new Uint8Array(buffer.slice(0, 16));
  if (normalized === 'image/png') return bytesMatch(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') return bytesMatch(bytes, [0xff, 0xd8, 0xff]);
  if (normalized === 'image/gif') return bytesMatch(bytes, [0x47, 0x49, 0x46, 0x38]);
  if (normalized === 'image/webp') {
    return bytesMatch(bytes, [0x52, 0x49, 0x46, 0x46]) && bytesMatch(bytes, [0x57, 0x45, 0x42, 0x50], 8);
  }
  if (normalized === 'image/svg+xml') {
    const ascii = String.fromCharCode(...bytes).trimStart().toLowerCase();
    return ascii.startsWith('<svg') || ascii.startsWith('<?xml');
  }
  return true;
}

function revokeObjectUrl(url?: string): void {
  if (!url || typeof URL === 'undefined' || !url.startsWith('blob:')) return;
  URL.revokeObjectURL(url);
}

function yieldToBrowser(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, 0));
}

function installMessageSyncV2SocketCapability(client: SkyChatSocketClient): void {
  const socket = client.socket as Socket & {
    prependAnyOutgoing?: (listener: (event: string, ...args: unknown[]) => void) => Socket;
    __skychatMessageSyncV2Patched?: boolean;
  };
  if (socket.__skychatMessageSyncV2Patched) return;
  socket.__skychatMessageSyncV2Patched = true;
  if (typeof socket.prependAnyOutgoing !== 'function') return;
  socket.prependAnyOutgoing((event: string, ...args: unknown[]) => {
    if (event !== 'auth' || !args[0] || typeof args[0] !== 'object') return;
    const auth = args[0] as Record<string, unknown>;
    const capabilities = auth.capabilities && typeof auth.capabilities === 'object'
      ? auth.capabilities as Record<string, unknown>
      : {};
    Object.assign(auth, {
      messageSyncV2: true,
      capabilities: { ...capabilities, messageSyncV2: true }
    });
  });
}
export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [cacheReady, setCacheReady] = useState(false);
  const [accountReady, setAccountReady] = useState(false);
  const [accountRefreshing, setAccountRefreshing] = useState(false);
  const [accountRefreshError, setAccountRefreshError] = useState<string | null>(null);
  const [initialSyncReady, setInitialSyncReady] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [config, setConfig] = useState<RuntimeConfig>({ apiBaseUrl: runtimeApiBaseUrl(), appName: 'SkyChat' });
  const [gameServiceConfig, setGameServiceConfig] = useState<GameServiceConfig>(() => buildFallbackGameServiceConfig(runtimeApiBaseUrl()));
  const [preferences, setPreferences] = useState<MobilePreferences>({ ...DEFAULT_PREFERENCES, locale: browserLocale() });
  const [token, setToken] = useState<string | null>(null);
  const mobileDeviceSessionId = useMemo(() => getOrCreateMobileDeviceSessionId(), []);
  const [me, setMe] = useState<UserProfile | null>(null);
  const [peers, setPeers] = useState<PeerRecord[]>([]);
  const [bots, setBots] = useState<BotDefinition[]>([]);
  const [typingByPeer, setTypingByPeer] = useState<Record<string, TypingPeerEntry[]>>({});
  const [gameEventVersion, setGameEventVersion] = useState(0);
  const [chatBackground, setChatBackgroundState] = useState<StoredChatBackground | null>(null);
  const [webPushState, setWebPushState] = useState<WebPushState>(DEFAULT_WEB_PUSH_STATE);
  const [keypair, setKeypair] = useState<KeyPairJwk>({ myPubJwk: null, myPrivJwk: null });
  const [chat, dispatch] = useReducer(chatReducer, initialChatState);
  const storageRef = useRef<KeyValueStorage | null>(null);
  const chatRepositoryRef = useRef<ChatRepository | null>(null);
  const chatRepositoryOwnerRef = useRef('');
  const socketRef = useRef<SkyChatSocketClient | null>(null);
  // Kept as a compatibility mirror for UI/profile code. Transport readiness is
  // authoritative in MobileConnectionCoordinator and is epoch-based.
  const authenticatedSocketRef = useRef<Socket | null>(null);
  const connectionCoordinatorRef = useRef<MobileConnectionCoordinator | null>(null);
  const lifecycleForegroundRef = useRef(typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const lifecycleOnlineRef = useRef(typeof navigator === 'undefined' || navigator.onLine !== false);
  const deviceConnectionLastReportAtRef = useRef(0);
  const deviceConnectionReportPromiseRef = useRef<Promise<void> | null>(null);
  const lastSelfPresencePatchRef = useRef<RuntimePresencePatch | null>(null);
  const peerPresencePatchesRef = useRef<Record<string, RuntimePresencePatch | undefined>>({});
  const preferredPresenceRef = useRef('available');
  const pendingUploadFilesRef = useRef<Record<string, File | undefined>>({});
  const activeUploadXhrRef = useRef<XMLHttpRequest | null>(null);
  const uploadWindowWaitersRef = useRef<Set<() => void>>(new Set());
  const uploadQueueTailRef = useRef<Promise<void>>(Promise.resolve());
  const queuedUploadMessageIdsRef = useRef<Set<string>>(new Set());
  const appForegroundRef = useRef(typeof document === 'undefined' || document.visibilityState !== 'hidden');
  const socketOutboxRestoreKeyRef = useRef('');
  const uploadRestoreKeyRef = useRef('');
  const socketEventListenersRef = useRef<Map<string, Set<SocketEventHandler>>>(new Map());
  const gameSocketRef = useRef<Socket | null>(null);
  const chatP2PFileManagerRef = useRef<ReturnType<typeof createChatP2PFileManager> | null>(null);
  const mediaUrlCacheRef = useRef<Record<string, MediaUrlCacheEntry>>({});
  const mediaLoadPromisesRef = useRef<Record<string, Promise<string> | undefined>>({});
  const localSearchIndexRef = useRef<Record<string, LocalSearchIndexEntry | undefined>>({});
  const messageHydrationPromisesRef = useRef<Record<string, Promise<ChatMessage> | undefined>>({});
  const chatRef = useRef<ChatState>(initialChatState);
  const activePeerRef = useRef<string | null>(null);
  const activeChatViewRef = useRef<string | null>(null);
  const meRef = useRef<UserProfile | null>(null);
  const keypairRef = useRef<KeyPairJwk>({ myPubJwk: null, myPrivJwk: null });
  const peersRef = useRef<PeerRecord[]>([]);
  const tRef = useRef<(key: string, fallback?: string) => string>(() => '');
  const refreshPeersRef = useRef<() => Promise<void>>(async () => undefined);
  const applySelfWeatherUpdateRef = useRef<(weatherPayload: unknown) => Promise<void>>(async () => undefined);
  const applyFriendWeatherUpdateRef = useRef<(payload: Record<string, unknown>) => void>(() => undefined);
  const patchSelfProfileRef = useRef<(patch: Partial<UserProfile>) => void>(() => undefined);
  const patchPeerProfileRef = useRef<(userId: string, patch: Partial<UserProfile>) => void>(() => undefined);
  const patchContactSettingsRef = useRef<(peerId: string, settings: ContactSettings) => void>(() => undefined);
  const pushRestoreKeyRef = useRef('');
  const prefetchKeyRef = useRef('');
  const rehydrationKeyRef = useRef('');
  const peerLoadPromisesRef = useRef<Record<string, Promise<void> | undefined>>({});
  const syncAllPromiseRef = useRef<Promise<void> | null>(null);
  const syncAllConversationsRef = useRef<ConversationSyncFn>(async () => undefined);
  const initialSyncOwnerRef = useRef('');
  const initialSyncCompletedRef = useRef(false);
  const lastFullSyncAtRef = useRef(0);
  const messageSyncV2OwnerRef = useRef('');
  const messageSyncV2StateRef = useRef<MobileMessageSyncV2State | null>(null);
  const observedRealtimeInsertSeqsRef = useRef<number[]>([]);
  const lastMessageSyncV2AtRef = useRef(0);
  const pendingSendTimersRef = useRef<Record<string, number>>({});
  const socketOutboxRef = useRef<Record<string, PendingSocketSend | undefined>>({});
  const flushSocketOutboxRef = useRef<(expectedClient?: SkyChatSocketClient) => void>(() => undefined);
  const reliableMessageSenderRef = useRef<(peerId: string, message: ChatMessage) => Promise<void>>(async () => undefined);
  const pendingTypingRef = useRef<Record<string, boolean | undefined>>({});
  const pendingProfileBroadcastRef = useRef<Record<string, unknown> | null>(null);
  const typingSessionsRef = useRef<Record<string, Record<string, TypingSessionEntry>>>({});
  const typingExpiryCheckRef = useRef<(peerId: string, userId: string) => void>(() => undefined);

  const api = useMemo(() => {
    const client = new SkyChatApi({ baseUrl: config.apiBaseUrl, token, debug: config.debug });
    attachSeparatedGameApi(client, gameServiceConfig.apiBaseUrl || buildFallbackGameServiceConfig(config.apiBaseUrl).apiBaseUrl, token);
    return client;
  }, [config.apiBaseUrl, config.debug, gameServiceConfig.apiBaseUrl, token]);
  const t = useMemo(() => {
    const baseTranslator = createTranslator(preferences.locale, LOCALE_RESOURCES);
    return (key: string, fallback?: string) => mobileTranslation(preferences.locale, key) || baseTranslator(key, fallback);
  }, [preferences.locale]);

  const mobileReliabilityOwner = useCallback(() => {
    const userId = meRef.current?.id || me?.id || '';
    if (!token || !userId) return '';
    return `${normalizeBaseUrl(config.apiBaseUrl)}:${userId}`;
  }, [config.apiBaseUrl, me?.id, token]);

  function messageSyncV2Owner(): string {
    const userId = meRef.current?.id || me?.id || '';
    if (!token || !userId) return '';
    return `${normalizeBaseUrl(config.apiBaseUrl)}:${userId}`;
  }

  function defaultMessageSyncV2State(): MobileMessageSyncV2State {
    return {
      cursor: 0,
      bootstrapComplete: false,
      peerScopeIds: [],
      observedInsertSeqs: []
    };
  }

  async function loadMessageSyncV2State(): Promise<MobileMessageSyncV2State> {
    const owner = messageSyncV2Owner();
    if (!owner || !storageRef.current) return defaultMessageSyncV2State();
    if (messageSyncV2OwnerRef.current === owner && messageSyncV2StateRef.current) {
      return messageSyncV2StateRef.current;
    }

    const raw = await storageRef.current.get<MobileMessageSyncV2State>(`messageSyncV2:${owner}`).catch(() => null);
    const parsed = raw || defaultMessageSyncV2State();
    const state: MobileMessageSyncV2State = {
      cursor: Math.max(0, Number(parsed.cursor || 0) || 0),
      bootstrapComplete: parsed.bootstrapComplete === true,
      peerScopeIds: Array.isArray(parsed.peerScopeIds)
        ? [...new Set((parsed.peerScopeIds as string[]).map((id) => String(id || '')).filter(Boolean))].sort()
        : [],
      observedInsertSeqs: Array.isArray(parsed.observedInsertSeqs)
        ? [...new Set((parsed.observedInsertSeqs as number[])
          .map((seq) => Number(seq || 0))
          .filter((seq) => Number.isSafeInteger(seq) && seq > 0))].slice(-MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS)
        : []
    };
    messageSyncV2OwnerRef.current = owner;
    messageSyncV2StateRef.current = state;
    return state;
  }

  async function persistMessageSyncV2State(state: MobileMessageSyncV2State): Promise<void> {
    const owner = messageSyncV2Owner();
    if (!owner || !storageRef.current) return;
    const normalized: MobileMessageSyncV2State = {
      cursor: Math.max(0, Number(state.cursor || 0) || 0),
      bootstrapComplete: state.bootstrapComplete === true,
      peerScopeIds: [...new Set((state.peerScopeIds || []).filter(Boolean))].sort(),
      observedInsertSeqs: [...new Set((state.observedInsertSeqs || [])
        .map((seq) => Number(seq || 0))
        .filter((seq) => Number.isSafeInteger(seq) && seq > 0))].slice(-MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS)
    };
    messageSyncV2OwnerRef.current = owner;
    messageSyncV2StateRef.current = normalized;
    await storageRef.current.set(`messageSyncV2:${owner}`, normalized).catch(() => undefined);
  }

  function rememberRealtimeInsertSequence(rawSequence: unknown): void {
    const sequence = Number(rawSequence || 0);
    if (!Number.isSafeInteger(sequence) || sequence <= 0) return;
    const values = observedRealtimeInsertSeqsRef.current;
    if (values.includes(sequence)) return;
    values.push(sequence);
    if (values.length > MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS) {
      values.splice(0, values.length - MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS);
    }
  }

  const onSocketEvent = useCallback((event: string, handler: SocketEventHandler) => {
    let handlers = socketEventListenersRef.current.get(event);
    if (!handlers) {
      handlers = new Set();
      socketEventListenersRef.current.set(event, handlers);
    }
    handlers.add(handler);
    socketRef.current?.socket.on(event, handler);

    return () => {
      handlers?.delete(handler);
      if (handlers && handlers.size === 0) socketEventListenersRef.current.delete(event);
      socketRef.current?.socket.off(event, handler);
    };
  }, []);

  const attachRegisteredSocketListeners = useCallback((socket: Socket) => {
    socketEventListenersRef.current.forEach((handlers, event) => {
      handlers.forEach((handler) => socket.on(event, handler));
    });
  }, []);

  const socketIsReady = useCallback((client: SkyChatSocketClient | null = socketRef.current) => {
    return connectionCoordinatorRef.current?.isReady(client) === true;
  }, []);

  const notifyUploadWindowAvailable = useCallback(() => {
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    if (!appForegroundRef.current || !lifecycleOnlineRef.current || !visible) return;
    const waiters = [...uploadWindowWaitersRef.current];
    uploadWindowWaitersRef.current.clear();
    waiters.forEach((resolve) => resolve());
  }, []);

  const emitSocketEvent = useCallback((event: string, payload?: unknown, ack?: (...args: unknown[]) => void) => {
    socketRef.current?.emit(event, payload, ack);
  }, []);

  const emitTypingThroughSocket = useCallback((socketClient: SkyChatSocketClient, peerId: string, on: boolean) => {
    const currentMe = meRef.current;
    const typingStyle = currentMe?.typing_animation || 'default';
    const customTypingAnimationUrl = currentMe?.custom_typing_animation_url || '';
    socketClient.emit('typing', {
      toId: peerId,
      on,
      isTyping: on,
      // Send both modern and legacy field names, matching Web exactly.
      style: typingStyle,
      typingStyle,
      customTypingAnimationUrl,
      custom_typing_animation_url: customTypingAnimationUrl
    });
  }, []);

  const flushPendingTyping = useCallback((expectedClient?: SkyChatSocketClient) => {
    const socketClient = socketRef.current;
    if (
      !socketClient ||
      (expectedClient && socketClient !== expectedClient) ||
      !socketClient.socket.connected ||
      !socketIsReady(socketClient)
    ) return;
    Object.entries(pendingTypingRef.current).forEach(([peerId, on]) => {
      delete pendingTypingRef.current[peerId];
      if (on === true) emitTypingThroughSocket(socketClient, peerId, true);
    });
  }, [emitTypingThroughSocket, socketIsReady]);

  const syncTypingStateForPeer = useCallback((peerId: string) => {
    const sessions = typingSessionsRef.current[peerId] || {};
    const nextList = Object.values(sessions).map((session) => session.entry);
    setTypingByPeer((current) => {
      const existing = current[peerId] || [];
      if (sameTypingList(existing, nextList)) return current;
      if (!nextList.length) {
        if (!(peerId in current)) return current;
        const next = { ...current };
        delete next[peerId];
        return next;
      }
      return { ...current, [peerId]: nextList };
    });
  }, []);

  const scheduleTypingExpiry = useCallback((peerId: string, userId: string) => {
    const session = typingSessionsRef.current[peerId]?.[userId];
    if (!session) return;
    if (session.timerId) window.clearTimeout(session.timerId);
    const elapsed = Date.now() - session.lastTypingAt;
    const delay = Math.max(TYPING_CHECK_FLOOR_MS, TYPING_LEASE_MS - elapsed);
    session.timerId = window.setTimeout(() => {
      typingExpiryCheckRef.current(peerId, userId);
    }, delay);
  }, []);

  const clearTypingForSender = useCallback((peerId: string, userId: string) => {
    if (!peerId || !userId) return;
    const sessions = typingSessionsRef.current[peerId];
    const session = sessions?.[userId];
    if (!sessions || !session) return;
    if (session.timerId) window.clearTimeout(session.timerId);
    delete sessions[userId];
    if (!Object.keys(sessions).length) delete typingSessionsRef.current[peerId];
    syncTypingStateForPeer(peerId);
  }, [syncTypingStateForPeer]);

  const clearAllTypingSessions = useCallback(() => {
    Object.values(typingSessionsRef.current).forEach((sessions) => {
      Object.values(sessions).forEach((session) => {
        if (session.timerId) window.clearTimeout(session.timerId);
      });
    });
    typingSessionsRef.current = {};
    setTypingByPeer((current) => Object.keys(current).length ? {} : current);
  }, []);

  const showTypingForSender = useCallback((peerId: string, userId: string, entry: TypingPeerEntry) => {
    if (!peerId || !userId) return;
    const now = Date.now();
    if (!typingSessionsRef.current[peerId]) typingSessionsRef.current[peerId] = {};
    const sessions = typingSessionsRef.current[peerId];
    const existing = sessions[userId];
    if (existing) {
      existing.lastTypingAt = now;
      if (!sameTypingEntry(existing.entry, entry)) {
        existing.entry = entry;
        syncTypingStateForPeer(peerId);
      }
      scheduleTypingExpiry(peerId, userId);
      return;
    }

    sessions[userId] = { entry, lastTypingAt: now };
    syncTypingStateForPeer(peerId);
    scheduleTypingExpiry(peerId, userId);
  }, [scheduleTypingExpiry, syncTypingStateForPeer]);

  useEffect(() => {
    typingExpiryCheckRef.current = (peerId: string, userId: string) => {
      const session = typingSessionsRef.current[peerId]?.[userId];
      if (!session) return;
      const elapsed = Date.now() - session.lastTypingAt;
      if (elapsed >= TYPING_LEASE_MS) {
        clearTypingForSender(peerId, userId);
        return;
      }
      scheduleTypingExpiry(peerId, userId);
    };
  }, [clearTypingForSender, scheduleTypingExpiry]);

  useEffect(() => () => {
    Object.values(typingSessionsRef.current).forEach((sessions) => {
      Object.values(sessions).forEach((session) => {
        if (session.timerId) window.clearTimeout(session.timerId);
      });
    });
  }, []);

  const reportMobileDeviceConnection = useCallback(async (
    overrideToken?: string | null,
    options: { force?: boolean } = {}
  ) => {
    const authToken = overrideToken || token;
    if (
      previewMode
      || !authToken
      || !appForegroundRef.current
      || !lifecycleOnlineRef.current
    ) return;

    const now = Date.now();
    // Socket auth already registers the device on the server. This HTTP report is
    // only a low-frequency heartbeat/fallback and must never be triggered by
    // presence:sync, otherwise the server broadcasts presence:sync again.
    if (!options.force && now - deviceConnectionLastReportAtRef.current < 45000) return;
    if (deviceConnectionReportPromiseRef.current) return deviceConnectionReportPromiseRef.current;

    const task = postMobileDeviceConnection(config.apiBaseUrl, authToken, mobileDeviceSessionId)
      .then(() => {
        deviceConnectionLastReportAtRef.current = Date.now();
      })
      .catch(() => {
        // Device source is informational: never block chat startup or recovery.
      })
      .finally(() => {
        if (deviceConnectionReportPromiseRef.current === task) {
          deviceConnectionReportPromiseRef.current = null;
        }
      });
    deviceConnectionReportPromiseRef.current = task;
    return task;
  }, [config.apiBaseUrl, mobileDeviceSessionId, previewMode, token]);

  const reportMobileDeviceDisconnection = useCallback((overrideToken?: string | null) => {
    const authToken = overrideToken || token;
    if (previewMode || !authToken) return;
    deviceConnectionLastReportAtRef.current = 0;
    postMobileDeviceDisconnection(config.apiBaseUrl, authToken, mobileDeviceSessionId);
  }, [config.apiBaseUrl, mobileDeviceSessionId, previewMode, token]);

  const assetUrl = useCallback((path: string) => {
    if (!path) return '';
    if (/^(https?:)?\/\//i.test(path) || /^(data:|blob:)/i.test(path)) return path;
    return `${normalizeBaseUrl(config.apiBaseUrl)}${path.startsWith('/') ? path : `/${path}`}`;
  }, [config.apiBaseUrl]);

  function revokeCachedMediaUrl(slot: string): void {
    const cached = mediaUrlCacheRef.current[slot];
    if (!cached) return;
    revokeObjectUrl(cached.url);
    delete mediaUrlCacheRef.current[slot];
  }

  function revokeAllMediaUrls(): void {
    Object.values(mediaUrlCacheRef.current).forEach((entry) => revokeObjectUrl(entry.url));
    mediaUrlCacheRef.current = {};
  }

  function invalidateLocalSearchIndex(peerId?: string): void {
    if (!peerId) {
      localSearchIndexRef.current = {};
      return;
    }
    delete localSearchIndexRef.current[peerId];
  }

  function cacheMessage(peerId: string, message: ChatMessage): void {
    if (previewMode || !peerId || !message.id) return;
    invalidateLocalSearchIndex(peerId);
    void ensureChatRepository()?.put(peerId, normalizeChatP2PMessageForCache(normalizeChatMessageForClient(message))).catch(() => undefined);
  }

  function ensureChatRepository(userId = meRef.current?.id || me?.id || ''): ChatRepository | null {
    if (previewMode || !userId) return null;
    if (!chatRepositoryRef.current || chatRepositoryOwnerRef.current !== userId) {
      chatRepositoryRef.current = createChatRepository(`user:${userId}`);
      chatRepositoryOwnerRef.current = userId;
    }
    return chatRepositoryRef.current;
  }

  function cacheMessages(peerId: string, messages: ChatMessage[]): void {
    if (previewMode || !peerId || !messages.length) return;
    invalidateLocalSearchIndex(peerId);
    void ensureChatRepository()?.putMany(peerId, messages.map((message) => normalizeChatP2PMessageForCache(normalizeChatMessageForClient(message)))).catch(() => undefined);
  }

  function normalizeMessageForLocalThread(message: ChatMessage, ownUserId?: string): ChatMessage {
    const normalized = normalizeChatP2PMessageForCache(normalizeChatMessageForClient(message));
    if (!ownUserId || normalized.fromId !== ownUserId) return normalized;

    const readBy = normalizeReadBy((normalized as ChatMessage & { readBy?: unknown }).readBy);
    const hasRemoteReader = readBy.some((id) => id && id !== ownUserId);
    const status = normalizeDeliveryStatus(normalized.status);

    if (status === 'read' && !hasRemoteReader) {
      return { ...normalized, status: 'sent' };
    }
    if (status === 'delivered') {
      return { ...normalized, status: 'sent' };
    }
    if (!status) {
      return { ...normalized, status: 'sent' };
    }
    return normalized;
  }

  function peerFromCachedThread(peerId: string): PeerRecord {
    return {
      id: peerId,
      nickname: '',
      status: 'accepted',
      type: 'friend'
    };
  }

  function mergeCachedThreadPeers(cachedPeers: PeerRecord[], cachedThreadPeerIds: string[]): PeerRecord[] {
    if (!cachedThreadPeerIds.length) return cachedPeers;
    const map = new Map<string, PeerRecord>();
    cachedPeers.forEach((peer) => map.set(peer.id, peer));
    cachedThreadPeerIds.forEach((peerId) => {
      if (!map.has(peerId)) map.set(peerId, peerFromCachedThread(peerId));
    });
    return [...map.values()];
  }

  async function hydrateCachedConversations(userId: string, cachedPeers: PeerRecord[], options: { limit?: number; skipPeerIds?: string[] } = {}): Promise<string[]> {
    if (!userId) return [];
    const repository = ensureChatRepository(userId);
    if (!repository) return [];
    const repositoryPeerIds = await repository.listPeerIds().catch(() => [] as string[]);
    const skip = new Set(options.skipPeerIds || []);
    let peerIds = [...new Set([
      ...cachedPeers.map((peer) => peer.id),
      ...repositoryPeerIds
    ].filter(Boolean))].filter((peerId) => !skip.has(peerId));
    if (options.limit && options.limit > 0) peerIds = peerIds.slice(0, options.limit);
    const hydratedPeerIds: string[] = [];

    for (const peerId of peerIds) {
      const cached = await repository.list(peerId, { limit: MESSAGE_PAGE_SIZE }).catch(() => [] as ChatMessage[]);
      if (!cached.length) continue;
      const messages = lastMessagePage(cached.map((message) => normalizeMessageForLocalThread(message, userId)));
      hydratedPeerIds.push(peerId);
      dispatch({ type: 'merge-hydrate', peerId, messages });
      dispatch({
        type: 'set-history-meta',
        peerId,
        patch: {
          initialLoaded: true,
          initialLoading: false,
          initialError: null,
          hasMoreOlder: cached.length >= MESSAGE_PAGE_SIZE,
          oldestCursor: historyCursorFromMessages(messages)
        }
      });
      await yieldToBrowser();
    }

    return hydratedPeerIds.sort();
  }

  function lastMessagePage(messages: ChatMessage[]): ChatMessage[] {
    return sortMessages(messages).slice(-MESSAGE_PAGE_SIZE);
  }

  const persistMe = useCallback(async (profile: UserProfile | null) => {
    if (previewMode) return;
    if (!profile) {
      await storageRef.current?.remove(ME_CACHE_KEY);
      return;
    }
    await storageRef.current?.set(ME_CACHE_KEY, profile);
  }, [previewMode]);

  const persistPeers = useCallback(async (nextPeers: PeerRecord[]) => {
    if (previewMode) return;
    await storageRef.current?.set(PEERS_CACHE_KEY, nextPeers);
  }, [previewMode]);

  const replaceMe = useCallback((updater: UserProfile | null | ((current: UserProfile | null) => UserProfile | null)) => {
    if (typeof updater !== 'function') {
      meRef.current = updater;
      setMe(updater);
      void persistMe(updater);
      return;
    }
    setMe((current) => {
      const nextProfile = (updater as (current: UserProfile | null) => UserProfile | null)(current);
      meRef.current = nextProfile;
      void persistMe(nextProfile);
      return nextProfile;
    });
  }, [persistMe]);

  const replacePeers = useCallback((updater: PeerRecord[] | ((current: PeerRecord[]) => PeerRecord[])) => {
    let nextPeers: PeerRecord[] = [];
    setPeers((current) => {
      nextPeers = typeof updater === 'function'
        ? (updater as (current: PeerRecord[]) => PeerRecord[])(current)
        : updater;
      peersRef.current = nextPeers;
      return nextPeers;
    });
    void persistPeers(nextPeers);
  }, [persistPeers]);

  const patchPeerProfile = useCallback((userId: string, patch: Partial<UserProfile>) => {
    if (!userId) return;
    replacePeers((current) => current.map((peer) => {
      if (peer.id !== userId || peer.type === 'group') return peer;
      return { ...peer, ...patch };
    }));
  }, [replacePeers]);

  const patchContactSettings = useCallback((peerId: string, settings: ContactSettings) => {
    if (!peerId) return;
    replacePeers((current) => current.map((peer) => {
      if (peer.id !== peerId) return peer;
      const nextSettings = {
        remarkName: peer.remarkName ?? null,
        mutedUntil: peer.mutedUntil ?? null,
        muteMode: peer.muteMode || 'none',
        pinnedAt: peer.pinnedAt ?? null,
        ...(peer.contactSettings || {}),
        ...settings,
        peerId
      };
      return {
        ...peer,
        remarkName: nextSettings.remarkName ?? null,
        mutedUntil: nextSettings.mutedUntil ?? null,
        muteMode: nextSettings.muteMode || 'none',
        pinnedAt: nextSettings.pinnedAt ?? null,
        contactSettings: nextSettings
      };
    }));
  }, [replacePeers]);

  const patchSelfProfile = useCallback((patch: Partial<UserProfile>) => {
    replaceMe((current) => current ? { ...current, ...patch } : current);
    if (me?.id) patchPeerProfile(me.id, patch);
  }, [me?.id, patchPeerProfile, replaceMe]);

  useEffect(() => {
    patchSelfProfileRef.current = patchSelfProfile;
    patchPeerProfileRef.current = patchPeerProfile;
    patchContactSettingsRef.current = patchContactSettings;
  }, [patchContactSettings, patchPeerProfile, patchSelfProfile]);

  useEffect(() => {
    const presence = String(me?.presence || '').toLowerCase().trim();
    if (presence && !['offline', 'online'].includes(presence)) {
      preferredPresenceRef.current = normalizeConfiguredPresence(presence);
      if (me?.id) writePreferredPresence(config.apiBaseUrl, me.id, preferredPresenceRef.current);
    }
  }, [config.apiBaseUrl, me?.id, me?.presence]);

  function appendChatMessage(peerId: string, message: ChatMessage, ownUserId?: string): void {
    const normalized = normalizeMessageForLocalThread(message, ownUserId);
    const existing = chatRef.current.byPeer[peerId]?.find((item) => item.id === normalized.id);
    const merged = existing ? mergeChatMessages(existing, normalized) : normalized;
    dispatch({ type: 'append', peerId, message: merged, ownUserId });
    cacheMessage(peerId, merged);
  }

  function patchChatP2PMessage(peerId: string, messageId: string, patch: Partial<ChatMessage> & Record<string, unknown>): void {
    const existing = chatRef.current.byPeer[peerId]?.find((message) => message.id === messageId);
    if (!existing) return;
    const metaPatch = patch.meta && typeof patch.meta === 'object' ? patch.meta as Record<string, unknown> : null;
    const next = {
      ...existing,
      ...patch,
      meta: metaPatch
        ? { ...((existing.meta && typeof existing.meta === 'object') ? existing.meta as Record<string, unknown> : {}), ...metaPatch }
        : existing.meta
    } as ChatMessage;
    appendChatMessage(peerId, next, meRef.current?.id || '');
  }

  function saveP2PBlobForMessage(message: ChatMessage, peerId: string, blob: Blob): void {
    const slot = mediaCacheSlot(message, peerId);
    const signature = mediaCacheSignature(message, peerId, meRef.current?.id || '');
    revokeCachedMediaUrl(slot);
    mediaUrlCacheRef.current[slot] = { url: URL.createObjectURL(blob), signature };
    void savePersistentMedia(slot, signature, blob);
  }

  function getChatP2PFileManager() {
    if (chatP2PFileManagerRef.current) return chatP2PFileManagerRef.current;
    chatP2PFileManagerRef.current = createChatP2PFileManager({
      getSocket: () => socketRef.current,
      getPeer: (peerId) => peersRef.current.find((peer) => peer.id === peerId),
      getMeId: () => meRef.current?.id || '',
      t: (key, fallback) => tRef.current(key, fallback),
      notify: (message) => {
        if (message) console.info('[ChatP2PFile]', message);
      },
      appendOrPatchMessage: (peerId, message) => appendChatMessage(peerId, message, meRef.current?.id || ''),
      patchMessage: patchChatP2PMessage,
      saveReceivedBlob: saveP2PBlobForMessage,
      decryptIncomingBlob: async (record, encryptedBlob) => {
        const currentKeypair = keypairRef.current;
        const sender = peersRef.current.find((peer) => peer.id === record.fromId || peer.id === record.peerId);
        if (!currentKeypair.myPrivJwk || !sender || !('pubkey_jwk' in sender) || !sender.pubkey_jwk) {
          throw new Error(tRef.current('p2pFileMissingPeerKey', 'Missing peer key for P2P file'));
        }
        const plainBuffer = await decryptToArrayBuffer(currentKeypair.myPrivJwk, sender.id, sender.pubkey_jwk, record.iv, encryptedBlob);
        return new Blob([plainBuffer], { type: record.fileType || 'application/octet-stream' });
      },
      sendChatMessage: (message) => {
        const peerId = String(message.toId || '');
        if (peerId) void reliableMessageSenderRef.current(peerId, message);
      }
    });
    return chatP2PFileManagerRef.current;
  }

  function cacheAck(peerId: string | undefined, id: string, patch: { status: string; seq?: number; readBy?: string[]; readerId?: string; error?: string }): void {
    if (!id) return;
    const peerIds = peerId ? [peerId] : Object.keys(chatRef.current.byPeer);
    const currentMeId = meRef.current?.id || '';
    peerIds.forEach((itemPeerId) => {
      const existing = chatRef.current.byPeer[itemPeerId]?.find((message) => message.id === id);
      if (!existing) return;
      const readBy = patch.readBy || mergeReadBy((existing as ChatMessage & { readBy?: unknown }).readBy, patch.readerId);
      const hasRemoteReader = normalizeReadBy(readBy).some((reader) => reader && reader !== currentMeId);
      let status = normalizeDeliveryStatus(patch.status, normalizeDeliveryStatus(existing.status, 'sent')) || 'sent';
      if (status === 'read' && !hasRemoteReader) status = 'sent';
      if (status === 'delivered') status = 'sent';
      if (isChatP2PSuccessMessage(existing) && (status === 'failed' || status === 'sending')) return;
      cacheMessage(itemPeerId, {
        ...existing,
        status,
        seq: patch.seq ?? existing.seq,
        readBy,
        error: patch.error ?? (status === 'failed' ? existing.error : undefined)
      });
    });
  }

  function cachePeerReadUntil(peerId: string, readUntilTs: number, readerId?: string): void {
    const currentMeId = meRef.current?.id || '';
    if (!readerId || readerId === currentMeId) return;
    const updated = (chatRef.current.byPeer[peerId] || [])
      .filter((message) => message.fromId === currentMeId && (message.ts || 0) <= readUntilTs)
      .map((message) => ({
        ...message,
        status: message.status === 'sending' || message.status === 'failed' ? message.status : 'read',
        readBy: mergeReadBy((message as ChatMessage & { readBy?: unknown }).readBy, readerId)
      }));
    cacheMessages(peerId, updated);
  }

  function applyPeerReadUntilReceipt(peerId: string, readUntilTs: number, readerId?: string): void {
    const currentMeId = meRef.current?.id || '';
    if (!peerId || !readerId || readerId === currentMeId) return;
    (chatRef.current.byPeer[peerId] || [])
      .filter((message) => message.fromId === currentMeId && (message.ts || 0) <= readUntilTs)
      .forEach((message) => {
        if (!message.id || message.status === 'sending' || message.status === 'failed') return;
        dispatch({ type: 'ack', peerId, id: message.id, status: 'read', readerId });
      });
    cachePeerReadUntil(peerId, readUntilTs, readerId);
  }

  function cacheEdit(peerId: string, id: string, patch: Partial<ChatMessage>): void {
    const existing = chatRef.current.byPeer[peerId]?.find((message) => message.id === id);
    if (!existing) return;
    cacheMessage(peerId, {
      ...existing,
      ...patch,
      edited: true,
      editedTs: patch.editedTs ?? Date.now()
    });
  }

  function cacheDelete(peerId: string, id: string): void {
    const existing = chatRef.current.byPeer[peerId]?.find((message) => message.id === id);
    if (!existing) return;
    cacheMessage(peerId, { ...existing, type: 'deleted', plain: '', payload: null, file: undefined });
  }

  const clearPendingSendTimer = useCallback((id?: string) => {
    if (!id) return;
    const timer = pendingSendTimersRef.current[id];
    if (timer) window.clearTimeout(timer);
    delete pendingSendTimersRef.current[id];
  }, []);

  const persistOutboxEntry = useCallback(async (entry: PendingSocketSend): Promise<void> => {
    const owner = mobileReliabilityOwner();
    if (!owner || !entry.message?.id) return;
    await persistSocketOutboxEntry(owner, {
      peerId: entry.peerId,
      message: entry.message,
      authRetryCount: entry.authRetryCount,
      attemptCount: entry.attemptCount,
      createdAt: entry.createdAt
    });
  }, [mobileReliabilityOwner]);

  const removeOutboxEntry = useCallback((messageId: string) => {
    const owner = mobileReliabilityOwner();
    delete socketOutboxRef.current[messageId];
    clearPendingSendTimer(messageId);
    if (owner) void removePersistedSocketOutboxEntry(owner, messageId);
  }, [clearPendingSendTimer, mobileReliabilityOwner]);

  const pauseSocketOutbox = useCallback(() => {
    Object.values(socketOutboxRef.current).forEach((entry) => {
      if (!entry) return;
      entry.inFlightSessionKey = undefined;
      clearPendingSendTimer(entry.message.id);
      void persistOutboxEntry(entry);
    });
  }, [clearPendingSendTimer, persistOutboxEntry]);

  const applySocketMessageAck = useCallback((
    payload: Record<string, unknown>,
    metadata: { attemptNumber?: number } = {}
  ) => {
    const currentMe = meRef.current;
    const id = String(payload.id || '');
    if (!id) return;
    if (payload.ok === false) {
      const errorPayload = {
        id,
        peerId: String(payload.peerId || ''),
        code: String(payload.code || ''),
        error: String(payload.error || tRef.current('sendFailed', 'Send failed'))
      };
      const queued = socketOutboxRef.current[id];
      if (errorPayload.code === 'not_authenticated' && queued) {
        const rejectedAttempt = metadata.attemptNumber || queued.attemptCount;
        // The server intentionally sends both message:error and the Socket.IO
        // acknowledgement callback. Count that pair as one failed attempt.
        if (queued.lastAuthErrorAttempt === rejectedAttempt) return;
        queued.lastAuthErrorAttempt = rejectedAttempt;
        queued.authRetryCount += 1;
        queued.inFlightSessionKey = undefined;
        clearPendingSendTimer(id);
        void persistOutboxEntry(queued);
        const coordinator = connectionCoordinatorRef.current;
        const client = socketRef.current;
        if (client) coordinator?.handleAuthError(client.socket, 'message-not-authenticated');
        coordinator?.ensureReady('message-not-authenticated');
        if (queued.authRetryCount <= MAX_SOCKET_AUTH_RETRIES) return;
      }
      removeOutboxEntry(id);
      const existing = errorPayload.peerId
        ? chatRef.current.byPeer[errorPayload.peerId]?.find((message) => message.id === id)
        : Object.values(chatRef.current.byPeer).flat().find((message) => message.id === id);
      if (existing && isChatP2PSuccessMessage(existing)) return;
      dispatch({ type: 'ack', peerId: errorPayload.peerId || undefined, id, status: 'failed', error: errorPayload.error });
      cacheAck(errorPayload.peerId || undefined, id, { status: 'failed', error: errorPayload.error });
      return;
    }

    removeOutboxEntry(id);
    const peerId = String(payload.peerId || '');
    const readerId = String(payload.readerId || '');
    const status = safeReadStatus(payload.status, readerId, currentMe?.id || '', 'sent');
    if (status !== 'failed' && status !== 'sending') {
      delete pendingUploadFilesRef.current[id];
      const owner = mobileReliabilityOwner();
      if (owner) void removePendingUpload(owner, id);
    }
    const existing = peerId
      ? chatRef.current.byPeer[peerId]?.find((message) => message.id === id)
      : Object.values(chatRef.current.byPeer).flat().find((message) => message.id === id);
    if (existing && isChatP2PSuccessMessage(existing) && (status === 'failed' || status === 'sending')) return;
    dispatch({
      type: 'ack',
      peerId: peerId || undefined,
      id,
      status,
      seq: Number(payload.seq || 0) || undefined,
      readerId: readerId || undefined
    });
    cacheAck(peerId || undefined, id, {
      status,
      seq: Number(payload.seq || 0) || undefined,
      readerId: readerId || undefined
    });
  }, [clearPendingSendTimer, mobileReliabilityOwner, persistOutboxEntry, removeOutboxEntry]);

  const applySocketMessageError = useCallback((payload: Record<string, unknown>) => {
    applySocketMessageAck({ ...payload, ok: false });
  }, [applySocketMessageAck]);

  const trackPendingSend = useCallback((peerId: string, id: string, sessionKey: string) => {
    clearPendingSendTimer(id);
    pendingSendTimersRef.current[id] = window.setTimeout(() => {
      delete pendingSendTimersRef.current[id];
      const entry = socketOutboxRef.current[id];
      if (!entry || entry.inFlightSessionKey !== sessionKey) return;

      void (async () => {
        entry.inFlightSessionKey = undefined;
        await persistOutboxEntry(entry);

        const socketClient = socketRef.current;
        const connectionReady = !!socketClient && socketIsReady(socketClient);
        if (
          !appForegroundRef.current
          || !lifecycleOnlineRef.current
          || !connectionReady
        ) {
          connectionCoordinatorRef.current?.ensureReady('message-ack-timeout');
          return;
        }

        // ACK events can be lost while the server has already committed the
        // stable message id. Reconcile before every resend.
        const authoritative = await findMessageAlreadyStored(peerId, entry.message);
        if (socketOutboxRef.current[id] !== entry) return;
        if (authoritative) {
          removeOutboxEntry(id);
          appendChatMessage(peerId, authoritative, meRef.current?.id || '');
          delete pendingUploadFilesRef.current[id];
          const owner = mobileReliabilityOwner();
          if (owner) await removePendingUpload(owner, id);
          return;
        }

        if (entry.attemptCount < MAX_MESSAGE_SEND_ATTEMPTS) {
          window.setTimeout(
            () => flushSocketOutboxRef.current(socketClient),
            Math.min(2200, 350 * Math.max(1, entry.attemptCount))
          );
          return;
        }

        removeOutboxEntry(id);
        const error = t('sendTimedOut');
        dispatch({ type: 'ack', peerId, id, status: 'failed', error });
        cacheAck(peerId, id, { status: 'failed', error });
      })().catch(() => undefined);
    }, MESSAGE_ACK_TIMEOUT_MS);
  }, [
    clearPendingSendTimer,
    mobileReliabilityOwner,
    persistOutboxEntry,
    removeOutboxEntry,
    socketIsReady,
    t
  ]);

  const flushSocketOutbox = useCallback((expectedClient?: SkyChatSocketClient) => {
    const socketClient = socketRef.current;
    const coordinator = connectionCoordinatorRef.current;
    if (
      !socketClient
      || (expectedClient && socketClient !== expectedClient)
      || !coordinator?.isReady(socketClient)
      || !appForegroundRef.current
      || !lifecycleOnlineRef.current
    ) return;

    const sessionKey = coordinator.sessionKey();
    const queued = Object.values(socketOutboxRef.current)
      .filter((entry): entry is PendingSocketSend => !!entry)
      .sort((a, b) => a.createdAt - b.createdAt);

    queued.forEach((entry) => {
      const { message } = entry;
      if (entry.inFlightSessionKey === sessionKey) return;
      entry.inFlightSessionKey = sessionKey;
      entry.attemptCount += 1;
      entry.lastAttemptAt = Date.now();
      void persistOutboxEntry(entry);
      try {
        // Arm the timeout first so an immediate acknowledgement (mock server,
        // local bridge or very fast reconnect) can always cancel it.
        trackPendingSend(entry.peerId, message.id, sessionKey);
        const attemptNumber = entry.attemptCount;
        socketClient.emit('message:send', message, (...args: unknown[]) => {
          const payload = args[0] && typeof args[0] === 'object'
            ? args[0] as Record<string, unknown>
            : { ok: false, id: message.id, peerId: entry.peerId, error: 'Invalid message acknowledgement' };
          applySocketMessageAck(payload, { attemptNumber });
        });
      } catch {
        entry.inFlightSessionKey = undefined;
        clearPendingSendTimer(message.id);
        void persistOutboxEntry(entry);
        connectionCoordinatorRef.current?.ensureReady('message-send-exception');
      }
    });
  }, [applySocketMessageAck, clearPendingSendTimer, persistOutboxEntry, trackPendingSend]);

  useEffect(() => {
    flushSocketOutboxRef.current = flushSocketOutbox;
  }, [flushSocketOutbox]);

  const sendOrQueueSocketMessage = useCallback(async (peerId: string, message: ChatMessage) => {
    const existing = socketOutboxRef.current[message.id];
    const entry: PendingSocketSend = existing || {
      peerId,
      message,
      authRetryCount: 0,
      attemptCount: 0,
      createdAt: Date.now()
    };
    entry.peerId = peerId;
    entry.message = message;
    entry.inFlightSessionKey = undefined;
    socketOutboxRef.current[message.id] = entry;
    await persistOutboxEntry(entry);

    const coordinator = connectionCoordinatorRef.current;
    coordinator?.ensureReady('outbox-queued');
    const socketClient = socketRef.current;
    if (socketClient && coordinator?.isReady(socketClient)) flushSocketOutbox(socketClient);
  }, [flushSocketOutbox, persistOutboxEntry]);

  useEffect(() => {
    reliableMessageSenderRef.current = sendOrQueueSocketMessage;
  }, [sendOrQueueSocketMessage]);

  useEffect(() => () => {
    Object.values(pendingSendTimersRef.current).forEach((timer) => window.clearTimeout(timer));
    pendingSendTimersRef.current = {};
    pauseSocketOutbox();
    pendingTypingRef.current = {};
    uploadWindowWaitersRef.current.clear();
  }, [pauseSocketOutbox]);

  useEffect(() => () => {
    revokeAllMediaUrls();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const accent = normalizeAccentColor(preferences.accentColor);
    const secondary = accentSecondary(accent);
    root.classList.toggle('sky-theme-light', preferences.themeMode === 'light');
    root.classList.toggle('sky-theme-dark', preferences.themeMode === 'dark');
    root.dataset.skyTheme = preferences.themeMode;
    root.dataset.skyAccent = ACCENT_PRESETS.includes(accent as typeof ACCENT_PRESETS[number]) ? accent.slice(1) : 'custom';
    root.style.setProperty('--ion-color-primary', accent);
    root.style.setProperty('--ion-color-primary-rgb', accentRgb(accent));
    root.style.setProperty('--ion-color-secondary', secondary);
    root.style.setProperty('--ion-color-secondary-rgb', accentRgb(secondary));
    root.lang = preferences.locale === 'zh' ? 'zh-CN' : preferences.locale;
  }, [preferences.accentColor, preferences.locale, preferences.themeMode]);

  useEffect(() => {
    chatRef.current = chat;
    activePeerRef.current = chat.activePeerId;
  }, [chat]);

  useEffect(() => {
    meRef.current = me;
  }, [me]);

  useEffect(() => {
    if (previewMode || !me?.id) {
      chatRepositoryRef.current = null;
      chatRepositoryOwnerRef.current = '';
      return;
    }
    ensureChatRepository(me.id);
  }, [me?.id, previewMode]);

  useEffect(() => {
    keypairRef.current = keypair;
  }, [keypair]);

  useEffect(() => {
    peersRef.current = peers;
  }, [peers]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    void getChatBackground().then((background) => {
      if (!cancelled) setChatBackgroundState(background);
    });
    return () => { cancelled = true; };
  }, []);

  const navigateToChatFromNotification = useCallback((peerId: string) => {
    if (typeof window === 'undefined') return;
    window.location.assign(`/chat/${encodeURIComponent(peerId)}`);
  }, []);

  useEffect(() => bindServiceWorkerBridge({
    getKeys: () => keypairRef.current,
    getFriends: () => peersRef.current,
    onNavigateToChat: navigateToChatFromNotification
  }), [navigateToChatFromNotification]);

  useEffect(() => {
    if (previewMode || !ready || !token) return;
    void syncServiceWorkerState({
      keypair,
      peers,
      locale: preferences.locale,
      previewsEnabled: preferences.previewsEnabled
    }).catch(() => undefined);
  }, [keypair, peers, preferences.locale, preferences.previewsEnabled, previewMode, ready, token]);

  const refreshWebPushState = useCallback(async () => {
    setWebPushState(await getWebPushState());
  }, []);

  useEffect(() => {
    if (previewMode || !ready) return;
    if (!token || !preferences.webpushEnabled) {
      void refreshWebPushState();
      pushRestoreKeyRef.current = '';
      return;
    }

    const restoreKey = `${config.apiBaseUrl}:${token}`;
    if (pushRestoreKeyRef.current === restoreKey) return;
    pushRestoreKeyRef.current = restoreKey;

    let cancelled = false;
    void (async () => {
      await syncServiceWorkerState({
        keypair,
        peers,
        locale: preferences.locale,
        previewsEnabled: preferences.previewsEnabled
      });
      const next = await restoreWebPush(api);
      if (!cancelled) setWebPushState(next);
    })().catch((error) => {
      if (!cancelled) {
        setWebPushState((current) => ({
          ...current,
          error: error instanceof Error ? error.message : 'Push restore failed'
        }));
      }
    });

    return () => { cancelled = true; };
  }, [
    api,
    config.apiBaseUrl,
    keypair,
    peers,
    preferences.locale,
    preferences.previewsEnabled,
    preferences.webpushEnabled,
    previewMode,
    ready,
    refreshWebPushState,
    token
  ]);

  const loadMe = useCallback(async () => {
    if (!token) return null;
    const requestedAt = Date.now();
    api.setToken(token);
    const res = await api.me();
    if (res.ok === false) {
      if (isAuthFailureResult(res as Record<string, unknown>)) {
        throw new AuthSessionExpiredError(String(res.error || 'Authentication session expired'));
      }
      throw new Error(String(res.error || 'Could not load profile'));
    }
    let profile = profileFromResult(res as Record<string, unknown>);
    if (!profile?.id) return profile;

    const getProfileTimeZone = (api as SkyChatApi & { getProfileTimeZone?: SkyChatApi['getProfileTimeZone'] }).getProfileTimeZone;
    if (typeof getProfileTimeZone === 'function') {
      try {
        const timeZoneResponse = await getProfileTimeZone.call(api);
        const timeZone = (timeZoneResponse as unknown as { timeZone?: UserTimeZone }).timeZone;
        if (timeZoneResponse.ok !== false && timeZone?.id) {
          profile = { ...profile, ...profileTimeZonePatch(timeZone) };
        }
      } catch (error) {
        console.warn('[MobileProfile] Could not refresh profile time zone:', error);
      }
    }

    const rememberedPresence = readPreferredPresence(config.apiBaseUrl, profile.id);
    const configuredPresence = normalizeConfiguredPresence(
      profile.presence,
      rememberedPresence || preferredPresenceRef.current
    );
    preferredPresenceRef.current = configuredPresence;
    writePreferredPresence(config.apiBaseUrl, profile.id, configuredPresence);
    let mergedProfile: UserProfile = { ...profile, presence: configuredPresence };
    const runtimePresence = lastSelfPresencePatchRef.current;
    if (
      runtimePresence
      && runtimePresence.receivedAt >= requestedAt
      && Date.now() - runtimePresence.receivedAt <= PRESENCE_PATCH_MAX_AGE_MS
    ) {
      mergedProfile = { ...mergedProfile, ...runtimePresence.patch };
    }
    if (socketIsReady()) {
      mergedProfile = { ...mergedProfile, online: true, presence: preferredPresenceRef.current } as UserProfile;
    }
    replaceMe(mergedProfile);
    return mergedProfile;
  }, [api, config.apiBaseUrl, replaceMe, socketIsReady, token]);

  const refreshPeers = useCallback(async () => {
    if (!token) return;
    const requestedAt = Date.now();
    setAccountRefreshing(true);
    setAccountRefreshError(null);
    try {
      api.setToken(token);
      const [friendsRes, groupsRes] = await Promise.all([api.friends(), api.getGroups()]);
      if (friendsRes.ok === false) throw new Error(String(friendsRes.error || 'Friends refresh failed'));
      if (groupsRes.ok === false) throw new Error(String(groupsRes.error || 'Groups refresh failed'));
      const friends = (Array.isArray(friendsRes.friends) ? friendsRes.friends as PeerRecord[] : []).map((friend) => {
        const runtimePresence = peerPresencePatchesRef.current[friend.id];
        if (
          !runtimePresence
          || runtimePresence.receivedAt < requestedAt
          || Date.now() - runtimePresence.receivedAt > PRESENCE_PATCH_MAX_AGE_MS
        ) return friend;
        return { ...friend, ...runtimePresence.patch };
      });
      const groups = (Array.isArray(groupsRes.groups) ? groupsRes.groups as PeerRecord[] : []).map((group) => ({
        ...group,
        type: 'group' as const,
        status: 'accepted',
        weather: null
      })) as PeerRecord[];
      const nextPeers = mergePeers(friends, groups);
      peersRef.current = nextPeers;
      replacePeers(nextPeers);
    } catch (error) {
      setAccountRefreshError(error instanceof Error ? error.message : 'Could not refresh account data');
    } finally {
      setAccountRefreshing(false);
    }
  }, [api, replacePeers, token]);

  useEffect(() => {
    refreshPeersRef.current = refreshPeers;
  }, [refreshPeers]);

  const loadUserConfig = useCallback(async (profile?: UserProfile | null) => {
    if (!token) return;
    const profileConfig = (profile?.user_config || profile?.userConfig) as UserPreferences | undefined;
    let next = profileConfig;
    try {
      const res = await api.getUserConfig();
      next = ((res.config || (res.data as { config?: UserPreferences } | undefined)?.config || profileConfig) as UserPreferences | undefined);
    } catch {
      next = profileConfig;
    }
    if (!next) return;
    let normalized = normalizePreferences(next);
    setPreferences((current) => {
      normalized = normalizePreferences({ ...current, ...next });
      return normalized;
    });
    await storageRef.current?.set('preferences', normalized);
  }, [api, token]);

  const clearLocalAuthSession = useCallback(async () => {
    connectionCoordinatorRef.current?.stop('clear-auth-session');
    connectionCoordinatorRef.current = null;
    authenticatedSocketRef.current = null;
    deviceConnectionLastReportAtRef.current = 0;
    socketRef.current?.disconnect();
    gameSocketRef.current?.disconnect();
    clearLegacyAuthStorage();
    await storageRef.current?.remove('authToken');
    await storageRef.current?.remove(ME_CACHE_KEY);
    await storageRef.current?.remove(PEERS_CACHE_KEY);
    chatRepositoryRef.current = null;
    chatRepositoryOwnerRef.current = '';
    activeChatViewRef.current = null;
    peerLoadPromisesRef.current = {};
    syncAllPromiseRef.current = null;
    initialSyncOwnerRef.current = '';
    initialSyncCompletedRef.current = false;
    revokeAllMediaUrls();
    clearAllTypingSessions();
    activeUploadXhrRef.current?.abort();
    activeUploadXhrRef.current = null;
    pendingUploadFilesRef.current = {};
    queuedUploadMessageIdsRef.current.clear();
    socketOutboxRestoreKeyRef.current = '';
    uploadRestoreKeyRef.current = '';
    lastSelfPresencePatchRef.current = null;
    peerPresencePatchesRef.current = {};
    preferredPresenceRef.current = 'available';
    setInitialSyncReady(false);
    setAccountReady(false);
    setAccountRefreshing(false);
    setAccountRefreshError(null);
    setToken(null);
    replaceMe(null);
    replacePeers([]);
    dispatch({ type: 'reset' });
  }, [clearAllTypingSessions, replaceMe, replacePeers]);

  const applySelfWeatherUpdate = useCallback(async (weatherPayload: unknown) => {
    const weather = normalizeWeatherPatch(weatherPayload);
    if (!weather) return;
    const showWeather = typeof weather.showWeather === 'boolean'
      ? weather.showWeather
      : me?.show_weather !== false;
    patchSelfProfile({ weather, show_weather: showWeather });
    const weatherConfig = {
      showWeather,
      cityId: weather.cityId,
      cityName: weather.cityName,
      country: weather.country
    };
    let nextPreferences: MobilePreferences | null = null;
    setPreferences((current) => {
      nextPreferences = { ...current, weather: weatherConfig };
      return nextPreferences;
    });
    if (nextPreferences) await storageRef.current?.set('preferences', nextPreferences);
  }, [me?.show_weather, patchSelfProfile]);

  const applyFriendWeatherUpdate = useCallback((payload: Record<string, unknown>) => {
    const userId = String(payload.userId || '');
    if (!userId) return;
    const weather = normalizeWeatherPatch(payload.weather);
    patchPeerProfile(userId, {
      weather,
      show_weather: weather ? weather.showWeather !== false : false
    });
  }, [patchPeerProfile]);

  useEffect(() => {
    applySelfWeatherUpdateRef.current = applySelfWeatherUpdate;
    applyFriendWeatherUpdateRef.current = applyFriendWeatherUpdate;
  }, [applyFriendWeatherUpdate, applySelfWeatherUpdate]);

  const readPeerIdForMessage = useCallback((peerId: string, message: ChatMessage) => {
    const peer = peersRef.current.find((item) => item.id === peerId);
    return peer?.type === 'group' ? peerId : message.fromId;
  }, []);

  const canEmitReadReceipts = useCallback((peerId: string) => {
    if (!peerId || activeChatViewRef.current !== peerId) return false;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
    if (typeof document !== 'undefined' && typeof document.hasFocus === 'function' && !document.hasFocus()) return false;
    return true;
  }, []);

  const emitMessageRead = useCallback((peerId: string, message: ChatMessage) => {
    const currentMe = meRef.current;
    if (previewMode || !canEmitReadReceipts(peerId) || !message.id || !currentMe?.id || message.fromId === currentMe.id) return 0;
    const readPeerId = readPeerIdForMessage(peerId, message);
    socketRef.current?.emit('message:read', { id: message.id, peerId: readPeerId });
    return Number(message.ts || 0);
  }, [canEmitReadReceipts, previewMode, readPeerIdForMessage]);

  const emitReadReceipts = useCallback((peerId: string, messages: ChatMessage[]) => {
    let maxTs = 0;
    messages.forEach((message) => {
      const ts = emitMessageRead(peerId, message);
      if (ts > maxTs) maxTs = ts;
    });
    if (maxTs > 0 && canEmitReadReceipts(peerId)) socketRef.current?.markRead(peerId, maxTs);
    return maxTs;
  }, [canEmitReadReceipts, emitMessageRead]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      const [runtime, storage] = await Promise.all([loadRuntimeConfig(), createSecretStorage()]);
      if (cancelled) return;
      storageRef.current = storage;
      const preview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';
      if (preview) {
        setPreviewMode(true);
        setConfig(runtime);
        setPreferences({ ...DEFAULT_PREFERENCES, locale: browserLocale() });
        setToken('preview-token');
        replaceMe(PREVIEW_ME);
        replacePeers(PREVIEW_PEERS);
        setCacheReady(true);
        setReady(true);
        return;
      }
      const [serverOverride, authToken, storedPreferences, cachedProfile, cachedPeers, migrated, savedKeypair] = await Promise.all([
        storage.get<string>('serverUrl'),
        storage.get<string>('authToken'),
        storage.get<UserPreferences>('preferences'),
        storage.get<UserProfile>(ME_CACHE_KEY),
        storage.get<PeerRecord[]>(PEERS_CACHE_KEY),
        migrateLegacyKeypair(storage).catch(() => null),
        storage.get<KeyPairJwk>('keypair')
      ]);
      if (cancelled) return;
      const storedKeypair = migrated || savedKeypair;
      const restoredToken = authToken || legacyToken();
      if (storedKeypair?.myPrivJwk) {
        keypairRef.current = storedKeypair;
        setKeypair(storedKeypair);
      }
      if (restoredToken && cachedProfile?.id) {
        meRef.current = cachedProfile;
        setMe(cachedProfile);
      }
      let cachedPeerList = restoredToken && Array.isArray(cachedPeers) ? cachedPeers : [];
      if (cachedPeerList.length) {
        peersRef.current = cachedPeerList;
        setPeers(cachedPeerList);
      }
      let cachedThreadPeerIds: string[] = [];
      if (restoredToken && cachedProfile?.id) {
        cachedThreadPeerIds = await hydrateCachedConversations(cachedProfile.id, cachedPeerList, { limit: CACHE_BOOT_HYDRATE_LIMIT });
        cachedPeerList = mergeCachedThreadPeers(cachedPeerList, cachedThreadPeerIds);
        if (cachedPeerList.length) {
          peersRef.current = cachedPeerList;
          setPeers(cachedPeerList);
        }
      }
      const nextApiBaseUrl = serverOverride || runtimeApiBaseUrl(runtime.apiBaseUrl);
      const normalizedApiBaseUrl = normalizeBaseUrl(nextApiBaseUrl);
      setConfig({ ...runtime, apiBaseUrl: normalizedApiBaseUrl });
      loadGameServiceConfig(normalizedApiBaseUrl)
        .then((gameConfig) => { if (!cancelled) setGameServiceConfig(gameConfig); })
        .catch(() => { if (!cancelled) setGameServiceConfig(buildFallbackGameServiceConfig(normalizedApiBaseUrl)); });
      setPreferences(normalizePreferences({ locale: browserLocale(), ...(storedPreferences || {}) }));
      if (!restoredToken) {
        clearLegacyAuthStorage();
        await storage.remove('authToken');
        await storage.remove(ME_CACHE_KEY);
        await storage.remove(PEERS_CACHE_KEY);
        replaceMe(null);
        replacePeers([]);
        dispatch({ type: 'reset' });
      }
      setToken(restoredToken);
      if (restoredToken) {
        void postMobileDeviceConnection(normalizedApiBaseUrl, restoredToken, mobileDeviceSessionId).catch(() => undefined);
      }
      setCacheReady(true);
      setReady(true);
      if (restoredToken && cachedProfile?.id) {
        void hydrateCachedConversations(cachedProfile.id, peersRef.current, {
          limit: BACKGROUND_CACHE_HYDRATE_LIMIT,
          skipPeerIds: cachedThreadPeerIds
        }).then((backgroundPeerIds) => {
          if (cancelled || !backgroundPeerIds.length) return;
          replacePeers((current) => mergeCachedThreadPeers(current, backgroundPeerIds));
        }).catch(() => undefined);
      }
    }
    void boot();
    return () => { cancelled = true; };
  }, [mobileDeviceSessionId, replaceMe, replacePeers]);

  useEffect(() => {
    if (previewMode && ready) {
      Object.entries(PREVIEW_MESSAGES).forEach(([peerId, messages]) => dispatch({ type: 'hydrate', peerId, messages }));
      setAccountReady(true);
      setAccountRefreshing(false);
      setAccountRefreshError(null);
      return;
    }
    if (!token || !ready) {
      setAccountReady(false);
      setAccountRefreshing(false);
      return;
    }
    let cancelled = false;
    setAccountReady(!!meRef.current?.id);
    setAccountRefreshing(true);
    setAccountRefreshError(null);
    void (async () => {
      let canUseAccount = !!meRef.current?.id;
      try {
        const profile = await loadMe();
        canUseAccount = !!profile?.id;
        if (!canUseAccount) {
          if (!cancelled) await clearLocalAuthSession();
          return;
        }
        if (!cancelled) setAccountReady(true);
        await Promise.allSettled([refreshPeers(), loadUserConfig(profile)]);
      } catch (error) {
        if (error instanceof AuthSessionExpiredError) {
          if (!cancelled) await clearLocalAuthSession();
          canUseAccount = false;
          return;
        }
        const cachedProfile = meRef.current?.id ? meRef.current : null;
        canUseAccount = !!cachedProfile?.id;
        if (!cancelled) {
          setAccountRefreshError(error instanceof Error ? error.message : 'Could not refresh account data');
        }
      } finally {
        if (!cancelled) setAccountReady(canUseAccount);
        if (!cancelled) setAccountRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clearLocalAuthSession, loadMe, loadUserConfig, previewMode, ready, refreshPeers, token]);

  const hydrateIncoming = useCallback((message: ChatMessage, peerId: string): Promise<ChatMessage> => {
    const normalized = normalizeChatMessageForClient(message);
    const currentKeypair = keypairRef.current;
    const currentPeers = peersRef.current;
    const currentMe = meRef.current;
    const iv = normalized.iv;
    const encryptedPayload = normalized.payload;
    const privateKey = currentKeypair.myPrivJwk;
    if (normalized.plain || !iv || !encryptedPayload || !privateKey) {
      return Promise.resolve(normalized);
    }
    const hydrationKey = `${peerId}:${normalized.id}:${iv}:${encryptedData(encryptedPayload)}`;
    const existing = messageHydrationPromisesRef.current[hydrationKey];
    if (existing) return existing;

    const task = (async () => {
      try {
        const data = encryptedData(encryptedPayload);
        const groupPeer = currentPeers.find((peer) => peer.id === peerId && peer.type === 'group');
        if (groupPeer) {
          return { ...normalized, plain: await decryptTextFromGroup(peerId, { iv, data }) };
        }
        const otherId = normalized.fromId === currentMe?.id ? normalized.toId : normalized.fromId;
        const other = currentPeers.find((peer) => peer.id === otherId) || currentPeers.find((peer) => peer.id === peerId);
        if (other && 'pubkey_jwk' in other && other.pubkey_jwk) {
          return { ...normalized, plain: await decryptText(privateKey, other.id, other.pubkey_jwk, { iv, data }) };
        }
      } catch {
        return { ...normalized, plain: '[Encrypted message]' };
      }
      return normalized;
    })();
    messageHydrationPromisesRef.current[hydrationKey] = task;
    void task.finally(() => {
      if (messageHydrationPromisesRef.current[hydrationKey] === task) {
        delete messageHydrationPromisesRef.current[hydrationKey];
      }
    });
    return task;
  }, []);

  const hydrateMessagesForPeer = useCallback(async (peerId: string, messages: ChatMessage[]): Promise<ChatMessage[]> => {
    const currentMeId = meRef.current?.id || '';
    const normalized = messages.map((message) => normalizeMessageForLocalThread(message, currentMeId));
    if (!normalized.some(messageNeedsHydration)) return normalized;
    const hydrated: ChatMessage[] = [];
    for (let index = 0; index < normalized.length; index += MESSAGE_HYDRATION_BATCH_SIZE) {
      const slice = normalized.slice(index, index + MESSAGE_HYDRATION_BATCH_SIZE);
      const batch = await Promise.all(slice.map((message) => hydrateIncoming(message, peerId)));
      hydrated.push(...batch);
      if (index + MESSAGE_HYDRATION_BATCH_SIZE < normalized.length) await yieldToBrowser();
    }
    return hydrated;
  }, [hydrateIncoming]);

  const getLocalSearchMessages = useCallback(async (peerId: string): Promise<ChatMessage[]> => {
    if (!peerId) return [];
    const existing = localSearchIndexRef.current[peerId];
    if (existing && !existing.promise) {
      return mergeMessageLists(existing.messages, chatRef.current.byPeer[peerId] || []);
    }
    if (existing?.promise) {
      const pendingMessages = await existing.promise;
      return mergeMessageLists(pendingMessages, chatRef.current.byPeer[peerId] || []);
    }

    const entry: LocalSearchIndexEntry = { messages: [] };
    const promise = (async () => {
      const repository = ensureChatRepository();
      const cached = repository
        ? await repository.list(peerId, { limit: Number.MAX_SAFE_INTEGER }).catch(() => [] as ChatMessage[])
        : (chatRef.current.byPeer[peerId] || []);
      const hydrated = await hydrateMessagesForPeer(peerId, cached);
      if (repository && hydrated.some((message, index) => message !== cached[index])) {
        await repository.putMany(peerId, hydrated.map(normalizeChatMessageForClient)).catch(() => undefined);
      }
      const messages = mergeMessageLists(hydrated, chatRef.current.byPeer[peerId] || []);
      if (localSearchIndexRef.current[peerId] === entry) {
        localSearchIndexRef.current[peerId] = { messages };
      }
      return messages;
    })();

    entry.promise = promise;
    localSearchIndexRef.current[peerId] = entry;
    try {
      return await promise;
    } catch (error) {
      if (localSearchIndexRef.current[peerId] === entry) delete localSearchIndexRef.current[peerId];
      throw error;
    }
  }, [hydrateMessagesForPeer]);

  const searchLocalMessages = useCallback(async (peerId: string, query = '', date = ''): Promise<MessageSearchResult[]> => {
    const trimmedQuery = query.trim();
    if (!peerId || (!trimmedQuery && !date)) return [];

    const messages = await getLocalSearchMessages(peerId);
    const lowerQuery = trimmedQuery.toLowerCase();
    const dateStart = date ? new Date(`${date}T00:00:00`).getTime() : 0;
    const hasDate = !!date && Number.isFinite(dateStart);
    const dateEnd = hasDate ? dateStart + 24 * 60 * 60 * 1000 - 1 : Number.MAX_SAFE_INTEGER;

    return messages
      .filter((message) => {
        if (message.type === 'deleted' || message.type === 'system_profile_update') return false;
        const ts = Number(message.ts || 0);
        const matchesDate = !hasDate || (ts >= dateStart && ts <= dateEnd);
        const haystack = `${message.plain || ''} ${message.file?.name || ''}`.toLowerCase();
        const matchesQuery = !lowerQuery || haystack.includes(lowerQuery);
        return matchesDate && matchesQuery;
      })
      .map((message) => ({
        id: message.id,
        ts: message.ts,
        seq: message.seq,
        senderId: message.fromId,
        peerId,
        fromId: message.fromId,
        toId: message.toId,
        type: message.type,
        plain: message.plain,
        preview: message.plain || message.file?.name || message.type,
        file: message.file
      } as MessageSearchResult))
      .sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0));
  }, [getLocalSearchMessages]);

  const revealLocalMessage = useCallback(async (peerId: string, messageId: string): Promise<void> => {
    if (!peerId || !messageId || previewMode) return;
    if ((chatRef.current.byPeer[peerId] || []).some((message) => message.id === messageId)) return;
    const repository = ensureChatRepository();
    if (!repository) return;
    const cachedWindow = await repository.windowAround(peerId, messageId, { before: MESSAGE_PAGE_SIZE, after: MESSAGE_PAGE_SIZE })
      .catch(() => [] as ChatMessage[]);
    if (!cachedWindow.some((message) => message.id === messageId)) return;

    const windowMessages = await hydrateMessagesForPeer(peerId, cachedWindow);
    const combined = mergeHydratedMessageLists(chatRef.current.byPeer[peerId] || [], windowMessages);
    await repository.putMany(peerId, combined.map(normalizeChatMessageForClient)).catch(() => undefined);
    dispatch({ type: 'merge-hydrate', peerId, messages: combined });
    dispatch({
      type: 'set-history-meta',
      peerId,
      patch: {
        initialLoaded: true,
        initialLoading: false,
        initialError: null,
        hasMoreOlder: cachedWindow.length >= MESSAGE_PAGE_SIZE || (chatRef.current.historyMetaByPeer[peerId]?.hasMoreOlder === true),
        oldestCursor: historyCursorFromMessages(combined)
      }
    });
  }, [hydrateMessagesForPeer, previewMode]);

  const listLocalChatFiles = useCallback(async (peerId: string): Promise<ChatFileItem[]> => {
    if (!peerId) return [];
    const repository = ensureChatRepository();
    const cached = repository
      ? await repository.listFiles(peerId, { limit: Number.MAX_SAFE_INTEGER }).catch(() => [] as ChatMessage[])
      : [];
    const currentMeId = meRef.current?.id || '';
    const messages = mergeMessageLists(
      cached.map((message) => normalizeMessageForLocalThread(message, currentMeId)),
      chatRef.current.byPeer[peerId] || []
    );
    return messages
      .map((message) => chatMessageToFileItem(message, peerId))
      .filter((file): file is ChatFileItem => !!file)
      .sort((a, b) => Number(b.ts || 0) - Number(a.ts || 0));
  }, []);

  const loadOlderMessages = useCallback(async (peerId: string): Promise<void> => {
    const historyMeta = chatRef.current.historyMetaByPeer[peerId] || defaultHistoryMeta();
    if (historyMeta.loadingOlder || !historyMeta.oldestCursor) return;

    dispatch({ type: 'set-history-loading', peerId, loadingOlder: true });
    try {
      const res = await api.messagesHistory({
        peerId,
        beforeTs: historyMeta.oldestCursor.beforeTs,
        beforeSeq: historyMeta.oldestCursor.beforeSeq,
        limit: MESSAGE_PAGE_SIZE
      });
      if (res.ok === false) {
        dispatch({ type: 'set-history-loading', peerId, loadingOlder: false });
        return;
      }
      const raw = ((res.messages || (res.data as { messages?: ChatMessage[] } | undefined)?.messages || []) as ChatMessage[])
        .map(normalizeChatMessageForClient);
      const hydrated = await hydrateMessagesForPeer(peerId, raw);
      cacheMessages(peerId, hydrated);
      dispatch({
        type: 'prepend-history',
        peerId,
        messages: hydrated,
        hasMoreOlder: res.hasMore === true,
        oldestCursor: hydrated.length ? historyCursorFromMessages(hydrated) : historyMeta.oldestCursor
      });
    } catch {
      dispatch({ type: 'set-history-loading', peerId, loadingOlder: false });
    }
  }, [api, hydrateMessagesForPeer]);

  const loadLatestPeerPage = useCallback(async (peerId: string, options: { render?: boolean; markRead?: boolean; updateState?: boolean } = {}): Promise<void> => {
    if (previewMode || !token || !peerId) return;
    const render = options.render === true;
    const updateState = render || options.updateState === true;
    const markReadAfterLoad = options.markRead === true;
    if (render) {
      dispatch({
        type: 'set-history-meta',
        peerId,
        patch: { initialLoading: true, initialError: null }
      });
    }
    const repository = ensureChatRepository();
    const cached = await repository?.list(peerId, { limit: MESSAGE_PAGE_SIZE }).catch(() => [] as ChatMessage[]) || [];
    const hydratedCached = await hydrateMessagesForPeer(peerId, cached);
    let combined = lastMessagePage(mergeHydratedMessageLists(chatRef.current.byPeer[peerId] || [], hydratedCached));
    const hadLocalMessages = combined.length > 0;
    const existingIds = new Set(combined.map((message) => message.id).filter(Boolean));
    let initialLoadSucceeded = false;

    if (updateState && combined.length > 0) {
      combined = lastMessagePage(mergeHydratedMessageLists(chatRef.current.byPeer[peerId] || [], combined));
      dispatch({ type: 'merge-hydrate', peerId, messages: combined });
      dispatch({
        type: 'set-history-meta',
        peerId,
        patch: {
          initialLoaded: true,
          initialLoading: false,
          initialError: null,
          hasMoreOlder: hydratedCached.length >= MESSAGE_PAGE_SIZE,
          oldestCursor: historyCursorFromMessages(combined)
        }
      });
    }

    try {
      const syncCursorMessages = combined.filter(messageCountsForSyncCursor);
      const res = combined.length
        ? await api.messagesSync({ peerId, lastTs: maxMessageTs(syncCursorMessages), lastSeq: maxMessageSeq(syncCursorMessages) })
        : await api.messagesHistory({ peerId, beforeTs: Date.now() + 1, limit: MESSAGE_PAGE_SIZE });
      if (res.ok === false) throw new Error(String(res.error || 'Conversation load failed'));
      const raw = combined.length
        ? syncedMessagesFromResult(res as Record<string, unknown>)
        : (((res.messages || (res.data as { messages?: ChatMessage[] } | undefined)?.messages || []) as ChatMessage[])
          .map(normalizeChatMessageForClient));
      const hydrated = await hydrateMessagesForPeer(peerId, raw);
      const knownIds = new Set(existingIds);
      (chatRef.current.byPeer[peerId] || []).forEach((message) => {
        if (message.id) knownIds.add(message.id);
      });
      const newHydrated = hydrated.filter((message) => {
        if (!message.id || knownIds.has(message.id)) return false;
        knownIds.add(message.id);
        return true;
      });
      // HTTP/cache hydration must not replace messages that arrived through the socket
      // while this request was in flight.
      const mergedMessages = mergeHydratedMessageLists(chatRef.current.byPeer[peerId] || [], combined, hydrated);
      cacheMessages(peerId, mergedMessages);
      combined = lastMessagePage(mergedMessages);
      initialLoadSucceeded = true;

      if (updateState) {
        dispatch({
          type: 'merge-hydrate',
          peerId,
          messages: combined,
          unreadMessageIds: !render && hadLocalMessages ? newHydrated.map((message) => message.id) : undefined,
          ownUserId: meRef.current?.id || ''
        });
        dispatch({
          type: 'set-history-meta',
          peerId,
          patch: {
            initialLoaded: true,
            initialLoading: false,
            initialError: null,
            hasMoreOlder: res.hasMore === true || combined.length >= MESSAGE_PAGE_SIZE,
            oldestCursor: historyCursorFromMessages(combined)
          }
        });
      }
    } catch (error) {
      // Cached messages are enough to keep the conversation usable while offline.
      if (updateState) {
        dispatch({
          type: 'set-history-meta',
          peerId,
          patch: {
            initialLoaded: combined.length > 0,
            initialLoading: false,
            initialError: combined.length > 0 ? null : error instanceof Error ? error.message : 'Conversation load failed'
          }
        });
      }
    }

    if (markReadAfterLoad && canEmitReadReceipts(peerId) && (initialLoadSucceeded || combined.length > 0)) {
      const maxReadTs = emitReadReceipts(peerId, combined);
      if (maxReadTs <= 0) socketRef.current?.markRead(peerId, Date.now());
    }
  }, [api, canEmitReadReceipts, emitReadReceipts, hydrateMessagesForPeer, previewMode, token]);

  const prefetchPeerHistory = useCallback(async (peerId: string): Promise<void> => {
    if (peerLoadPromisesRef.current[peerId]) return peerLoadPromisesRef.current[peerId];
    const promise = loadLatestPeerPage(peerId, { updateState: true })
      .catch(() => undefined)
      .finally(() => {
        delete peerLoadPromisesRef.current[peerId];
      });
    peerLoadPromisesRef.current[peerId] = promise;
    return promise;
  }, [loadLatestPeerPage]);

  const syncAllConversationsLegacy = useCallback(async (): Promise<void> => {
    const map = new Map<string, PeerRecord>();
    peersRef.current.filter(isAcceptedConversationPeer).forEach((peer) => map.set(peer.id, peer));
    Object.keys(chatRef.current.byPeer).forEach((peerId) => {
      if (peerId && !map.has(peerId)) map.set(peerId, peerFromCachedThread(peerId));
    });
    const peersToSync = [...map.values()].sort((a, b) => {
      const aMessages = chatRef.current.byPeer[a.id] || [];
      const bMessages = chatRef.current.byPeer[b.id] || [];
      return peerLastMessageTime(b, bMessages) - peerLastMessageTime(a, aMessages);
    });
    if (!peersToSync.length) return;

    let cursor = 0;
    const runWorker = async () => {
      while (cursor < peersToSync.length) {
        const peer = peersToSync[cursor];
        cursor += 1;
        if (peer?.id) await prefetchPeerHistory(peer.id);
        await yieldToBrowser();
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(FULL_SYNC_CONCURRENCY, peersToSync.length) }, () => runWorker())
    );
  }, [prefetchPeerHistory]);

  const syncAllConversations = useCallback(async (options: ConversationSyncOptions = {}): Promise<void> => {
    if (previewMode || !token || !meRef.current?.id) return;
    if (syncAllPromiseRef.current) {
      await syncAllPromiseRef.current;
      // An initial account refresh may replace the peer scope while a socket-driven
      // checkpoint is already running. The UI gate must cover the refreshed scope,
      // so force one coalesced follow-up after the earlier checkpoint completes.
      if (options.force) await syncAllConversations(options);
      return;
    }

    const promise = (async () => {
      const now = Date.now();
      const syncState = await loadMessageSyncV2State();
      const currentScopeIds = peersRef.current
        .filter(isAcceptedConversationPeer)
        .map((peer) => peer.id)
        .filter(Boolean)
        .sort();
      const previousScope = new Set(syncState.peerScopeIds || []);
      const addedScopeIds = currentScopeIds.filter((peerId) => !previousScope.has(peerId));
      const scopeChanged = currentScopeIds.length !== syncState.peerScopeIds.length
        || currentScopeIds.some((peerId, index) => peerId !== syncState.peerScopeIds[index]);
      const needsBootstrap = !syncState.bootstrapComplete || addedScopeIds.length > 0;

      // Avoid the initial + socket-authenticated + peers effects issuing the same
      // checkpoint back-to-back. Scope changes bypass this debounce because newly
      // visible group/friend history needs a reconciliation immediately.
      if (!needsBootstrap && now - lastMessageSyncV2AtRef.current < MESSAGE_SYNC_V2_MIN_INTERVAL_MS) {
        if (scopeChanged) {
          syncState.peerScopeIds = currentScopeIds;
          await persistMessageSyncV2State(syncState);
        }
        return;
      }
      if (!options.force && !needsBootstrap && now - lastFullSyncAtRef.current < FULL_SYNC_THROTTLE_MS) return;

      type BootstrapPlan = {
        baselineCursor: number;
        fetchSeqs: number[];
        fetchIds: string[];
      };

      const catchUpPeerForBootstrap = async (peerId: string): Promise<void> => {
        const repository = ensureChatRepository();
        const cached = repository
          ? await repository.list(peerId, { limit: MESSAGE_PAGE_SIZE }).catch(() => [] as ChatMessage[])
          : [];
        const local = mergeHydratedMessageLists(
          chatRef.current.byPeer[peerId] || [],
          cached.map((message) => normalizeMessageForLocalThread(message, meRef.current?.id || ''))
        );
        const cursorMessages = local.filter(messageCountsForSyncCursor);
        const response = local.length
          ? await api.messagesSync({
            peerId,
            lastTs: maxMessageTs(cursorMessages),
            lastSeq: maxMessageSeq(cursorMessages)
          })
          : await api.messagesHistory({ peerId, beforeTs: Date.now() + 1, limit: MESSAGE_PAGE_SIZE });
        if (response?.ok === false) throw new Error(String(response.error || 'Bootstrap peer catch-up failed'));

        const raw = local.length
          ? syncedMessagesFromResult(response as Record<string, unknown>)
          : (((response.messages || (response.data as { messages?: ChatMessage[] } | undefined)?.messages || []) as ChatMessage[])
            .map(normalizeChatMessageForClient));
        const hydrated = await hydrateMessagesForPeer(peerId, raw);
        const merged = mergeHydratedMessageLists(chatRef.current.byPeer[peerId] || [], local, hydrated);
        if (repository && merged.length) {
          await repository.putMany(peerId, merged.map((message) => normalizeChatP2PMessageForCache(normalizeChatMessageForClient(message))));
        }
        if (merged.length) {
          dispatch({ type: 'merge-hydrate', peerId, messages: lastMessagePage(merged), ownUserId: meRef.current?.id || '' });
          dispatch({
            type: 'set-history-meta',
            peerId,
            patch: {
              initialLoaded: true,
              initialLoading: false,
              initialError: null,
              hasMoreOlder: response.hasMore === true || merged.length >= MESSAGE_PAGE_SIZE,
              oldestCursor: historyCursorFromMessages(lastMessagePage(merged))
            }
          });
        }
      };

      const buildBootstrapPlan = async (): Promise<BootstrapPlan | 'unsupported' | null> => {
        let manifest;
        try {
          manifest = await requestMessageSyncV2Manifest(config.apiBaseUrl, token);
        } catch {
          return null;
        }
        if (manifest.unsupported) return 'unsupported';
        if (!manifest.ok) return null;

        // The mobile client intentionally keeps a lazy/bounded history. Before
        // adopting the account-wide cursor, catch up only conversations that were
        // already part of the mobile sync surface. On first migration this matches
        // the old behavior once; later scope changes touch only newly added peers.
        const peersToCatchUp = syncState.bootstrapComplete ? addedScopeIds : currentScopeIds;
        for (const peerId of peersToCatchUp) {
          await catchUpPeerForBootstrap(peerId);
          await yieldToBrowser();
        }

        const repository = ensureChatRepository();
        const repositoryPeerIds = repository
          ? await repository.listPeerIds().catch(() => [] as string[])
          : [];
        const localPeerIds = [...new Set([
          ...repositoryPeerIds,
          ...currentScopeIds,
          ...Object.keys(chatRef.current.byPeer)
        ].filter(Boolean))];
        const localSequences = new Set<number>();
        const localSequencesWithReactions = new Set<number>();
        const localLegacyIds = new Set<string>();

        for (const peerId of localPeerIds) {
          const cached = repository
            ? await repository.list(peerId, { limit: Number.MAX_SAFE_INTEGER }).catch(() => [] as ChatMessage[])
            : [];
          const localMessages = mergeMessageLists(cached, chatRef.current.byPeer[peerId] || []);
          for (const message of localMessages) {
            const sequence = Number(message.seq || 0) || 0;
            if (sequence > 0) {
              localSequences.add(sequence);
              const record = message as ChatMessage & Record<string, unknown>;
              const reactions = record.reactions;
              if ((Array.isArray(reactions) && reactions.length > 0) || (!Array.isArray(reactions) && !!reactions)) {
                localSequencesWithReactions.add(sequence);
              }
            } else if (message.id) localLegacyIds.add(message.id);
          }
          await yieldToBrowser();
        }

        const fetchSeqSet = new Set<number>();
        const fetchIdSet = new Set<string>();
        const serverSequences = new Set((manifest.sequences || [])
          .map((seq) => Number(seq || 0))
          .filter((seq) => Number.isSafeInteger(seq) && seq > 0));
        const mutableItems = manifest.mutable || (manifest.items || []).filter((item) =>
          Number(item.seq || 0) > 0 && (item.edited === true || item.deleted === true || Number(item.reactionCount || 0) > 0));
        for (const item of mutableItems) {
          const seq = Number(item.seq || 0) || 0;
          if (seq > 0 && localSequences.has(seq)) fetchSeqSet.add(seq);
        }
        // If a locally cached message carries reaction data but the server now has
        // none, requesting its current snapshot also clears stale local reactions.
        for (const seq of localSequencesWithReactions) {
          if (serverSequences.has(seq)) fetchSeqSet.add(seq);
        }
        const legacyItems = manifest.legacy || (manifest.items || []).filter((item) => Number(item.seq || 0) <= 0);
        for (const item of legacyItems) {
          const id = String(item.id || '');
          if (id && localLegacyIds.has(id)) fetchIdSet.add(id);
        }

        // Never turn a bounded mobile cache into a full-history download. The API
        // accepts at most these explicit bootstrap sets; older uncached history is
        // still loaded normally when the user scrolls back.
        const fetchSeqs = [...fetchSeqSet].sort((a, b) => b - a).slice(0, 4096).sort((a, b) => a - b);
        const fetchIds = [...fetchIdSet].slice(-1024);
        return {
          baselineCursor: Math.max(0, Number(manifest.watermark || 0) || 0),
          fetchSeqs,
          fetchIds
        };
      };

      const applySyncV2Messages = async (messages: Array<ChatMessage & { syncSeq?: number; syncKind?: string }>): Promise<void> => {
        const currentMeId = meRef.current?.id || '';
        const ordered = [...messages].sort((a, b) => {
          const syncDiff = (Number(a.syncSeq || 0) || 0) - (Number(b.syncSeq || 0) || 0);
          if (syncDiff) return syncDiff;
          const tsDiff = Number(a.ts || 0) - Number(b.ts || 0);
          if (tsDiff) return tsDiff;
          return String(a.id || '').localeCompare(String(b.id || ''));
        });
        const repository = ensureChatRepository();
        const localIdCache = new Map<string, Set<string>>();

        const localIdsForPeer = async (peerId: string): Promise<Set<string>> => {
          const existing = localIdCache.get(peerId);
          if (existing) return existing;
          const cached = repository
            ? await repository.list(peerId, { limit: Number.MAX_SAFE_INTEGER }).catch(() => [] as ChatMessage[])
            : [];
          const ids = new Set([
            ...cached.map((message) => message.id),
            ...(chatRef.current.byPeer[peerId] || []).map((message) => message.id)
          ].filter(Boolean));
          localIdCache.set(peerId, ids);
          return ids;
        };

        for (const rawMessage of ordered) {
          const normalized = normalizeChatMessageForClient(rawMessage);
          const fromId = String(normalized.fromId || '');
          const toId = String(normalized.toId || '');
          const peerId = fromId === currentMeId
            ? toId
            : toId === currentMeId
              ? fromId
              : toId || fromId;
          if (!peerId || !normalized.id) continue;

          const ids = await localIdsForPeer(peerId);
          const currentExists = (chatRef.current.byPeer[peerId] || []).some((message) => message.id === normalized.id);
          const localExists = ids.has(normalized.id);
          const syncKind = String(rawMessage.syncKind || 'insert').toLowerCase();
          const isInsert = syncKind === 'insert';

          // Mutating an old message that is outside the mobile cache should not
          // drag that old history into the active window. When the user later
          // loads that page, /messages/history returns the authoritative snapshot.
          if (!isInsert && !localExists) continue;

          const hydrated = (await hydrateMessagesForPeer(peerId, [normalized]))[0] || normalized;
          const stored = normalizeChatP2PMessageForCache(normalizeChatMessageForClient(hydrated));
          if (repository) await repository.put(peerId, stored);
          ids.add(stored.id);

          if (currentExists || (!localExists && isInsert)) {
            appendChatMessage(peerId, hydrated, currentMeId);
          }
        }
      };

      let bootstrapPlan: BootstrapPlan | null = null;
      if (needsBootstrap) {
        const plan = await buildBootstrapPlan();
        if (plan === 'unsupported') {
          lastFullSyncAtRef.current = now;
          await syncAllConversationsLegacy();
          return;
        }
        if (!plan) return;
        bootstrapPlan = plan;
      }

      const knownInsertSnapshot = [...new Set([
        ...(syncState.observedInsertSeqs || []),
        ...observedRealtimeInsertSeqsRef.current
      ])].slice(-MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS);
      let cursor = bootstrapPlan ? 0 : Math.max(0, syncState.cursor);
      let resetHandled = false;
      while (true) {
        const requestedCursor = cursor;
        let response;
        try {
          response = await requestMessageSyncV2(config.apiBaseUrl, token, {
            cursor,
            limit: MESSAGE_SYNC_V2_PAGE_SIZE,
            knownInsertSeqs: knownInsertSnapshot,
            manifestBootstrap: !!bootstrapPlan && bootstrapPlan.baselineCursor > 0,
            bootstrapBaselineCursor: bootstrapPlan?.baselineCursor || 0,
            bootstrapFetchAll: false,
            bootstrapFetchSeqs: bootstrapPlan?.fetchSeqs,
            bootstrapFetchIds: bootstrapPlan?.fetchIds
          });
        } catch {
          return;
        }

        if (response.unsupported) {
          lastFullSyncAtRef.current = now;
          await syncAllConversationsLegacy();
          return;
        }
        if (!response.ok) return;

        if (response.cursorReset) {
          if (resetHandled) return;
          resetHandled = true;
          syncState.cursor = 0;
          syncState.bootstrapComplete = false;
          syncState.observedInsertSeqs = [];
          await persistMessageSyncV2State(syncState);
          observedRealtimeInsertSeqsRef.current = [];
          const plan = await buildBootstrapPlan();
          if (!plan || plan === 'unsupported') {
            if (plan === 'unsupported') await syncAllConversationsLegacy();
            return;
          }
          bootstrapPlan = plan;
          cursor = 0;
          continue;
        }

        await applySyncV2Messages(response.messages || []);
        cursor = Math.max(cursor, Number(response.nextCursor || 0) || 0);
        syncState.cursor = cursor;

        if (response.bootstrapComplete === true) {
          syncState.bootstrapComplete = true;
          syncState.peerScopeIds = currentScopeIds;
          bootstrapPlan = null;
        } else if (!needsBootstrap && scopeChanged) {
          syncState.peerScopeIds = currentScopeIds;
        }

        if (response.hasMore !== true) {
          const acknowledged = new Set(knownInsertSnapshot);
          syncState.observedInsertSeqs = syncState.observedInsertSeqs.filter((seq) => !acknowledged.has(seq));
          observedRealtimeInsertSeqsRef.current = observedRealtimeInsertSeqsRef.current.filter((seq) => !acknowledged.has(seq));
        } else {
          syncState.observedInsertSeqs = [...new Set([
            ...syncState.observedInsertSeqs,
            ...observedRealtimeInsertSeqsRef.current
          ])].slice(-MESSAGE_SYNC_V2_MAX_KNOWN_INSERTS);
        }
        await persistMessageSyncV2State(syncState);

        if (response.hasMore !== true) break;
        if (cursor <= requestedCursor) return;
      }

      lastMessageSyncV2AtRef.current = Date.now();
      lastFullSyncAtRef.current = Date.now();
    })().catch(() => undefined).finally(() => {
      syncAllPromiseRef.current = null;
    });

    syncAllPromiseRef.current = promise;
    await promise;
    // Let batched history dispatches commit before the initial UI gate opens.
    if (options.force) await yieldToBrowser();
  }, [
    api,
    config.apiBaseUrl,
    hydrateMessagesForPeer,
    prefetchPeerHistory,
    previewMode,
    syncAllConversationsLegacy,
    token
  ]);

  useEffect(() => {
    syncAllConversationsRef.current = syncAllConversations;
  }, [syncAllConversations]);

  useEffect(() => {
    const ownerKey = previewMode ? 'preview' : token ? `token:${token}` : 'anonymous';
    if (initialSyncOwnerRef.current !== ownerKey) {
      initialSyncOwnerRef.current = ownerKey;
      initialSyncCompletedRef.current = false;
    }

    if (previewMode && ready) {
      initialSyncCompletedRef.current = true;
      setInitialSyncReady(true);
      return;
    }
    if (!ready) {
      initialSyncCompletedRef.current = false;
      setInitialSyncReady(false);
      return;
    }
    if (!token) {
      initialSyncCompletedRef.current = true;
      setInitialSyncReady(true);
      return;
    }
    if (accountRefreshError && !accountRefreshing && !me?.id) {
      initialSyncCompletedRef.current = true;
      setInitialSyncReady(true);
      return;
    }
    if (!accountReady || accountRefreshing || !me?.id) {
      if (!initialSyncCompletedRef.current) setInitialSyncReady(false);
      return;
    }

    let cancelled = false;
    if (!initialSyncCompletedRef.current) setInitialSyncReady(false);
    void syncAllConversations({ reason: 'initial', force: true })
      .finally(() => {
        if (!cancelled) {
          initialSyncCompletedRef.current = true;
          setInitialSyncReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    accountReady,
    accountRefreshError,
    accountRefreshing,
    me?.id,
    previewMode,
    ready,
    syncAllConversations,
    token
  ]);


  useEffect(() => {
    if (previewMode || !keypair.myPrivJwk) return;
    const pending = Object.entries(chat.byPeer)
      .map(([peerId, messages]) => {
        const ids = messages.filter(messageNeedsHydration).map((message) => message.id).join(',');
        return ids ? `${peerId}:${ids}` : '';
      })
      .filter(Boolean);
    if (!pending.length) return;
    const metadataKey = peers.map((peer) => `${peer.id}:${peer.type === 'group' ? 'g' : 'p'}:${'pubkey_jwk' in peer && peer.pubkey_jwk ? 'k' : ''}`).join('|');
    const rehydrationKey = `${metadataKey}::${pending.join('|')}`;
    if (rehydrationKeyRef.current === rehydrationKey) return;
    rehydrationKeyRef.current = rehydrationKey;

    let cancelled = false;
    void (async () => {
      for (const peerId of Object.keys(chatRef.current.byPeer)) {
        if (cancelled) return;
        const currentMessages = chatRef.current.byPeer[peerId] || [];
        if (!currentMessages.some(messageNeedsHydration)) continue;
        const hydrated = await hydrateMessagesForPeer(peerId, currentMessages);
        if (cancelled) return;
        if (!hydrated.some((message, index) => message.plain && message.plain !== currentMessages[index]?.plain)) continue;
        const merged = mergeHydratedMessageLists(chatRef.current.byPeer[peerId] || [], currentMessages, hydrated);
        cacheMessages(peerId, merged.filter((message) => !!message.plain));
        dispatch({ type: 'merge-hydrate', peerId, messages: merged });
      }
    })().catch(() => undefined);

    return () => { cancelled = true; };
  }, [chat.byPeer, hydrateMessagesForPeer, keypair.myPrivJwk, peers, previewMode]);

  useEffect(() => {
    if (previewMode || !token || !accountReady || !me?.id || !peers.length) return;
    const acceptedPeers = peers
      .filter(isAcceptedConversationPeer)
      .sort((a, b) => {
        const aMessages = chatRef.current.byPeer[a.id] || [];
        const bMessages = chatRef.current.byPeer[b.id] || [];
        return peerLastMessageTime(b, bMessages) - peerLastMessageTime(a, aMessages);
      });
    if (!acceptedPeers.length) return;

    const prefetchKey = `${me.id}:${acceptedPeers.map((peer) => `${peer.id}:${Number((peer as Record<string, unknown>).lastMessageTime || 0) || 0}`).join('|')}`;
    if (prefetchKeyRef.current === prefetchKey) return;
    prefetchKeyRef.current = prefetchKey;

    let cancelled = false;
    const startTimer = window.setTimeout(() => {
      if (!cancelled) void syncAllConversations({ reason: 'peers' });
    }, 650);

    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
    };
  }, [accountReady, me?.id, peers, previewMode, syncAllConversations, token]);

  useEffect(() => {
    if (previewMode || !config.apiBaseUrl) return;
    let cancelled = false;
    loadGameServiceConfig(config.apiBaseUrl)
      .then((gameConfig) => { if (!cancelled) setGameServiceConfig(gameConfig); })
      .catch(() => { if (!cancelled) setGameServiceConfig(buildFallbackGameServiceConfig(config.apiBaseUrl)); });
    return () => { cancelled = true; };
  }, [config.apiBaseUrl, previewMode]);

  useEffect(() => {
    if (previewMode) return;
    if (!token) {
      clearAllTypingSessions();
      pendingTypingRef.current = {};
      authenticatedSocketRef.current = null;
      connectionCoordinatorRef.current?.stop('token-cleared');
      connectionCoordinatorRef.current = null;
      socketOutboxRef.current = {};
      socketRef.current?.disconnect();
      socketRef.current = null;
      gameSocketRef.current?.disconnect();
      gameSocketRef.current = null;
      return;
    }
    if (!me?.id) {
      authenticatedSocketRef.current = null;
      connectionCoordinatorRef.current?.stop('identity-unavailable');
      connectionCoordinatorRef.current = null;
      socketRef.current?.disconnect();
      socketRef.current = null;
      gameSocketRef.current?.disconnect();
      gameSocketRef.current = null;
      return;
    }

    connectionCoordinatorRef.current?.stop('socket-replaced');
    connectionCoordinatorRef.current = null;
    socketRef.current?.disconnect();
    authenticatedSocketRef.current = null;
    let socket!: SkyChatSocketClient;
    const hydrateAppendedIncoming = (message: ChatMessage, peerId: string, ownUserId: string) => {
      const normalized = normalizeChatMessageForClient(message);
      if (!messageNeedsHydration(normalized)) return;
      // Match Web: the realtime packet is visible immediately, while decryption only
      // patches that same message id when it finishes.
      void hydrateIncoming(normalized, peerId).then((hydrated) => {
        if (socketRef.current !== socket || meRef.current?.id !== ownUserId || !hydrated.plain) return;
        const existing = chatRef.current.byPeer[peerId]?.find((message) => message.id === hydrated.id);
        if (existing && (
          !messageNeedsHydration(existing) ||
          existing.iv !== normalized.iv ||
          encryptedData(existing.payload) !== encryptedData(normalized.payload)
        )) return;
        const cached = existing
          ? mergeHydratedMessageLists([existing], [hydrated]).find((message) => message.id === hydrated.id) || hydrated
          : hydrated;
        cacheMessage(peerId, cached);
        // merge-hydrate preserves newer edits, deletes and delivery state that may have
        // arrived while decryption was running.
        dispatch({ type: 'merge-hydrate', peerId, messages: [hydrated] });
      }).catch(() => undefined);
    };
    socket = createSkyChatSocket(config.apiBaseUrl, token, {
      onMessage: (message) => {
        const currentMe = meRef.current;
        if (!currentMe?.id) return;
        const currentPeers = peersRef.current;
        const peerId = currentPeers.some((peer) => peer.id === message.toId && peer.type === 'group')
          ? message.toId
          : peerIdForMessage(message, currentMe.id);
        if (message.fromId && message.fromId !== currentMe.id) clearTypingForSender(peerId, message.fromId);
        const normalized = normalizeChatMessageForClient(message);
        rememberRealtimeInsertSequence(normalized.seq);
        appendChatMessage(peerId, normalized, currentMe.id);
        hydrateAppendedIncoming(normalized, peerId, currentMe.id);
        if (canEmitReadReceipts(peerId)) {
          const readTs = emitMessageRead(peerId, normalized);
          if (readTs > 0) socketRef.current?.markRead(peerId, readTs);
        }
      },
      onSync: (message) => {
        const currentMe = meRef.current;
        if (!currentMe?.id) return;
        const currentPeers = peersRef.current;
        const peerId = currentPeers.some((peer) => peer.id === message.toId && peer.type === 'group')
          ? message.toId
          : peerIdForMessage(message, currentMe.id);
        if (message.fromId && message.fromId !== currentMe.id) clearTypingForSender(peerId, message.fromId);
        const normalized = normalizeChatMessageForClient(message);
        rememberRealtimeInsertSequence(normalized.seq);
        appendChatMessage(peerId, normalized, currentMe.id);
        hydrateAppendedIncoming(normalized, peerId, currentMe.id);
      },
      onAck: (payload) => {
        rememberRealtimeInsertSequence(payload.seq);
        applySocketMessageAck({ ...payload, ok: true });
      },
      onError: (payload) => {
        applySocketMessageError(payload as Record<string, unknown>);
      },
      onStatus: (payload) => {
        rememberRealtimeInsertSequence(payload.seq);
        const id = String(payload.id || '');
        if (!id) return;
        const readerId = String(payload.readerId || '');
        applySocketMessageAck({
          ...payload,
          ok: true,
          id,
          status: safeReadStatus(payload.status, readerId, meRef.current?.id || '', 'delivered'),
          readerId: readerId || undefined
        });
      },
      onReadStatusSync: (payload) => {
        const data = (payload || {}) as {
          peerId?: string;
          readUntilTs?: number;
          ts?: number;
          readerId?: string;
          userId?: string;
          fromId?: string;
        };
        const peerId = String(data.peerId || '');
        const readUntilTs = Number(data.readUntilTs || data.ts || 0);
        const readerId = String(data.readerId || data.userId || data.fromId || '');
        if (peerId && Number.isFinite(readUntilTs) && readUntilTs > 0) {
          applyPeerReadUntilReceipt(peerId, readUntilTs, readerId || undefined);
        }
      },
      onAllReadStatusesSync: (payload) => {
        Object.entries(payload || {}).forEach(([peerId, value]) => {
          const record = value && typeof value === 'object' ? value as Record<string, unknown> : null;
          const readUntilTs = Number(record?.readUntilTs || record?.ts || value || 0);
          const readerId = String(record?.readerId || record?.userId || record?.fromId || '');
          if (peerId && Number.isFinite(readUntilTs) && readUntilTs > 0) {
            applyPeerReadUntilReceipt(peerId, readUntilTs, readerId || undefined);
          }
        });
      },
      onTyping: (payload) => {
        const currentMe = meRef.current;
        const currentPeers = peersRef.current;
        const data = (payload || {}) as Record<string, unknown>;
        const fromId = String(data.fromId || '');
        const toId = String(data.toId || '');
        if (!fromId || fromId === currentMe?.id) return;
        const groupPeer = currentPeers.find((peer) => peer.id === toId && peer.type === 'group');
        const peerId = groupPeer ? toId : fromId;
        const on = data.on === true || data.isTyping === true;
        const friend = currentPeers.find((peer) => peer.id === fromId && peer.type !== 'group') as UserProfile | undefined;
        const entry: TypingPeerEntry = resolveIncomingTypingEntry(data, friend, fromId);
        if (on) showTypingForSender(peerId, fromId, entry);
        else scheduleTypingExpiry(peerId, fromId);
      },
      onFriendsRefresh: () => {
        void refreshPeersRef.current()
          .then(() => syncAllConversationsRef.current({ reason: 'friends-refresh', force: true }))
          .catch(() => undefined);
      },
      onContactSettingsUpdate: (payload) => {
        const settings = payload.settings;
        if (settings?.peerId) {
          patchContactSettingsRef.current(settings.peerId, settings);
        }
      },
      onGroupChange: () => {
        void refreshPeersRef.current()
          .then(() => syncAllConversationsRef.current({ reason: 'group-change', force: true }))
          .catch(() => undefined);
      },
      onGameEvent: () => {
        setGameEventVersion((value) => value + 1);
        void refreshPeersRef.current();
      }
    }, { deviceKind: MOBILE_DEVICE_KIND, deviceSessionId: mobileDeviceSessionId });

    // createSkyChatSocket emits auth from its own connect listener. Patch the raw
    // socket before the event loop can connect so that first auth packet already
    // advertises sync-v2 and the server can suppress duplicate durable replays.
    installMessageSyncV2SocketCapability(socket);
    socketRef.current = socket;
    const connection = new MobileConnectionCoordinator({
      getClient: () => socketRef.current === socket ? socket : null,
      getToken: () => token,
      getForeground: () => appForegroundRef.current && lifecycleForegroundRef.current,
      getOnline: () => lifecycleOnlineRef.current,
      deviceKind: MOBILE_DEVICE_KIND,
      deviceSessionId: mobileDeviceSessionId,
      onReady: (readyClient) => {
        if (socketRef.current !== socket || readyClient !== socket) return;
        authenticatedSocketRef.current = socket.socket;
        patchSelfProfileRef.current({
          online: true,
          presence: normalizeConfiguredPresence(preferredPresenceRef.current)
        } as Partial<UserProfile>);
        void reportMobileDeviceConnection();
        flushSocketOutbox(socket);
        flushPendingTyping(socket);
        if (pendingProfileBroadcastRef.current) {
          socket.emit('profile:broadcast', { profile: pendingProfileBroadcastRef.current });
          pendingProfileBroadcastRef.current = null;
        }
        socket.emit('bots:get');
        window.setTimeout(() => {
          if (connectionCoordinatorRef.current !== connection || !connection.isReady(socket)) return;
          void loadMe().catch(() => undefined);
          void refreshPeersRef.current().catch(() => undefined);
          void syncAllConversationsRef.current({ reason: 'socket-authenticated', force: true }).catch(() => undefined);
        }, FOREGROUND_SYNC_DELAY_MS);
      },
      onUnavailable: () => {
        if (authenticatedSocketRef.current === socket.socket) authenticatedSocketRef.current = null;
        pauseSocketOutbox();
      }
    });
    connectionCoordinatorRef.current = connection;

    getChatP2PFileManager().attach(socket.socket);

    socket.socket.on('message:edit', (payload: Record<string, unknown>) => {
      const currentMe = meRef.current;
      const currentPeers = peersRef.current;
      const currentKeypair = keypairRef.current;
      const id = String(payload.id || '');
      const fromId = String(payload.fromId || '');
      const toId = String(payload.toId || '');
      const groupPeer = currentPeers.find((peer) => peer.id === toId && peer.type === 'group');
      const peerId = groupPeer ? toId : fromId === currentMe?.id ? toId : fromId;
      if (!id || !peerId) return;
      void (async () => {
        let plain = typeof payload.plain === 'string' ? payload.plain : undefined;
        const iv = typeof payload.iv === 'string' ? payload.iv : undefined;
        const data = encryptedData(payload.payload);
        if (!plain && iv && data) {
          try {
            if (groupPeer) {
              plain = await decryptTextFromGroup(peerId, { iv, data });
            } else {
              const otherId = fromId === currentMe?.id ? toId : fromId;
              const other = peersRef.current.find((peer) => peer.id === otherId);
              if (other && 'pubkey_jwk' in other && other.pubkey_jwk && currentKeypair.myPrivJwk) {
                plain = await decryptText(currentKeypair.myPrivJwk, other.id, other.pubkey_jwk, { iv, data });
              }
            }
          } catch {
            plain = '[Encrypted message]';
          }
        }
        const editedTs = Number(payload.editedTs || Date.now());
        dispatch({ type: 'edit', peerId, id, plain, iv, payload: payload.payload, editedTs });
        cacheEdit(peerId, id, { plain, iv, payload: payload.payload, editedTs });
      })();
    });
    socket.socket.on('message:delete', (payload: Record<string, unknown>) => {
      const currentMe = meRef.current;
      const fromId = String(payload.fromId || '');
      const toId = String(payload.toId || '');
      const id = String(payload.id || '');
      const peerId = fromId === currentMe?.id ? toId : fromId;
      if (id && peerId) {
        dispatch({ type: 'delete', peerId, id });
        cacheDelete(peerId, id);
      }
    });
    socket.socket.on('auth:ok', (payload: Record<string, unknown> = {}) => {
      const confirmedSocketId = String(payload.socketId || '');
      connection.confirmAuthenticated(socket.socket, 'auth:ok', {
        socketId: confirmedSocketId || String(socket.socket.id || '')
      });
    });
    socket.socket.on('bots:list', (payload: unknown) => {
      // Legacy servers predate auth:ok but publish this only after auth succeeds.
      connection.confirmAuthenticated(socket.socket, 'bots:list');
      setBots(normalizeBotList(payload));
    });
    socket.socket.on('bot:message:update', (message: ChatMessage) => {
      const currentMe = meRef.current;
      if (!currentMe?.id) return;
      const normalized = normalizeChatMessageForClient(message);
      const currentPeers = peersRef.current;
      const peerId = currentPeers.some((peer) => peer.id === normalized.toId && peer.type === 'group')
        ? normalized.toId
        : peerIdForMessage(normalized, currentMe.id);
      appendChatMessage(peerId, normalized, currentMe.id);
    });
    socket.socket.on('weather:update', (weather: unknown) => {
      void applySelfWeatherUpdateRef.current(weather);
    });
    socket.socket.on('friend:weather', (payload: Record<string, unknown>) => {
      applyFriendWeatherUpdateRef.current(payload);
    });
    socket.socket.on('presence:update', (payload: Record<string, unknown>) => {
      const currentMe = meRef.current;
      const userId = String(payload.userId || payload.id || '');
      if (!userId) return;
      const receivedAt = Date.now();
      const revision = socketPresenceRevision(payload);
      if (currentMe?.id && userId === currentMe.id) {
        const currentRuntime = lastSelfPresencePatchRef.current;
        if (currentRuntime && revision < currentRuntime.revision) return;
        const patch = presencePatchFromSocketPayload(payload, preferredPresenceRef.current);
        const nextPatch = { ...(currentRuntime?.patch || {}), ...patch };
        if (nextPatch.presence) preferredPresenceRef.current = normalizeConfiguredPresence(nextPatch.presence, preferredPresenceRef.current);
        lastSelfPresencePatchRef.current = { patch: nextPatch, receivedAt, revision };
        patchSelfProfileRef.current(nextPatch);
      } else {
        const currentRuntime = peerPresencePatchesRef.current[userId];
        if (currentRuntime && revision < currentRuntime.revision) return;
        const currentPeer = peersRef.current.find((peer) => peer.id === userId);
        const currentPresence = currentPeer?.type !== 'group' ? currentPeer?.presence : undefined;
        const patch = presencePatchFromSocketPayload(payload, currentPresence || 'available');
        const nextPatch = { ...(currentRuntime?.patch || {}), ...patch };
        peerPresencePatchesRef.current[userId] = { patch: nextPatch, receivedAt, revision };
        patchPeerProfileRef.current(userId, nextPatch);
      }
    });
    socket.socket.on('presence:sync', (payload: Record<string, unknown>) => {
      // Compatibility authentication confirmation only. Never POST
      // /api/device-connection from here: that endpoint broadcasts presence:sync.
      connection.confirmAuthenticated(socket.socket, 'presence:sync');
      const receivedAt = Date.now();
      const revision = socketPresenceRevision(payload);
      const currentRuntime = lastSelfPresencePatchRef.current;
      if (!currentRuntime || revision >= currentRuntime.revision) {
        const patch = presencePatchFromSocketPayload(payload, preferredPresenceRef.current);
        const nextPatch = {
          ...(currentRuntime?.patch || {}),
          ...patch,
          online: true,
          presence: normalizeConfiguredPresence(
            (patch as Record<string, unknown>).presence || preferredPresenceRef.current,
            preferredPresenceRef.current
          )
        } as Partial<UserProfile>;
        preferredPresenceRef.current = normalizeConfiguredPresence(nextPatch.presence, preferredPresenceRef.current);
        lastSelfPresencePatchRef.current = { patch: nextPatch, receivedAt, revision };
        patchSelfProfileRef.current(nextPatch);
      }
    });
    socket.socket.on('profile:update', (payload: Record<string, unknown>) => {
      const currentMe = meRef.current;
      const userId = String(payload.userId || '');
      const profile = (payload.profile || {}) as UserProfile;
      if (!userId || !profile) return;
      if (currentMe?.id && userId === currentMe.id) {
        let selfProfile: Partial<UserProfile> = profile;
        if (hasPresencePayload(profile as Record<string, unknown>)) {
          const patch = presencePatchFromSocketPayload(profile as Record<string, unknown>, preferredPresenceRef.current);
          const receivedAt = Date.now();
          const revision = socketPresenceRevision(profile as Record<string, unknown>);
          const currentRuntime = lastSelfPresencePatchRef.current;
          if (!currentRuntime || revision >= currentRuntime.revision) {
            const nextPatch = { ...(currentRuntime?.patch || {}), ...patch };
            if (nextPatch.presence) preferredPresenceRef.current = normalizeConfiguredPresence(nextPatch.presence, preferredPresenceRef.current);
            lastSelfPresencePatchRef.current = { patch: nextPatch, receivedAt, revision };
            selfProfile = { ...profile, ...nextPatch };
          }
        }
        patchSelfProfileRef.current(selfProfile);
      } else {
        const previousPeer = peersRef.current.find((peer) => peer.id === userId);
        const systemMessage = createProfileUpdateSystemMessage(userId, previousPeer, profile, currentMe?.id || '');
        patchPeerProfileRef.current(userId, profile);
        if (systemMessage) appendChatMessage(userId, systemMessage, currentMe?.id);
      }
    });
    socket.socket.on('connect', () => {
      if (socketRef.current !== socket) return;
      authenticatedSocketRef.current = null;
      pauseSocketOutbox();
      connection.handleTransportConnect(socket.socket, 'socket-connect');
    });
    socket.socket.on('disconnect', (reason) => {
      if (authenticatedSocketRef.current === socket.socket) authenticatedSocketRef.current = null;
      pauseSocketOutbox();
      // A transport interruption must not overwrite the configured presence with
      // offline. The server applies its own grace period and remains authoritative.
      connection.handleTransportDisconnect(socket.socket, `socket-disconnect:${String(reason || '')}`);
    });
    socket.socket.on('auth:error', () => {
      if (authenticatedSocketRef.current === socket.socket) authenticatedSocketRef.current = null;
      pauseSocketOutbox();
      connection.handleAuthError(socket.socket, 'auth:error');
    });
    attachRegisteredSocketListeners(socket.socket);

    connection.start('socket-created');

    const gameSocket = connectSeparatedGameSocket(gameServiceConfig, token, () => {
      setGameEventVersion((value) => value + 1);
      void refreshPeersRef.current();
    }, { deviceKind: MOBILE_DEVICE_KIND, deviceSessionId: mobileDeviceSessionId });
    gameSocketRef.current = gameSocket;

    return () => {
      connection.stop('socket-effect-cleanup');
      if (connectionCoordinatorRef.current === connection) connectionCoordinatorRef.current = null;
      if (socketRef.current === socket) socketRef.current = null;
      if (authenticatedSocketRef.current === socket.socket) authenticatedSocketRef.current = null;
      if (gameSocketRef.current === gameSocket) gameSocketRef.current = null;
      socket.disconnect();
      gameSocket.disconnect();
    };
  }, [
    applySocketMessageAck,
    applySocketMessageError,
    attachRegisteredSocketListeners,
    canEmitReadReceipts,
    clearAllTypingSessions,
    clearTypingForSender,
    config.apiBaseUrl,
    emitMessageRead,
    flushPendingTyping,
    flushSocketOutbox,
    gameServiceConfig.apiBaseUrl,
    gameServiceConfig.socketPath,
    gameServiceConfig.socketUrl,
    hydrateIncoming,
    loadMe,
    me?.id,
    mobileDeviceSessionId,
    pauseSocketOutbox,
    previewMode,
    reportMobileDeviceConnection,
    scheduleTypingExpiry,
    showTypingForSender,
    token
  ]);

  useEffect(() => {
    if (previewMode || !token) return undefined;

    let recoveryGeneration = 0;
    let refreshTimer: number | null = null;
    const scheduleForegroundRefresh = (reason: string) => {
      const generation = ++recoveryGeneration;
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        const coordinator = connectionCoordinatorRef.current;
        if (!coordinator) return;
        void coordinator.waitUntilReady(15000, `lifecycle:${reason}`)
          .then(() => {
            if (generation !== recoveryGeneration || !appForegroundRef.current) return;
            void loadMe().catch(() => undefined);
            void refreshPeersRef.current().catch(() => undefined);
            void syncAllConversationsRef.current({ reason: 'foreground', force: true }).catch(() => undefined);
            flushSocketOutboxRef.current(socketRef.current || undefined);
          })
          .catch(() => undefined);
      }, 180);
    };

    const lifecycle = installMobileLifecycleCoordinator({
      onBackground: (reason) => {
        lifecycleForegroundRef.current = false;
        appForegroundRef.current = false;
        recoveryGeneration += 1;
        authenticatedSocketRef.current = null;
        pauseSocketOutbox();
        activeUploadXhrRef.current?.abort();
        connectionCoordinatorRef.current?.suspend(`lifecycle:${reason}`);
      },
      onForeground: (reason) => {
        lifecycleForegroundRef.current = true;
        appForegroundRef.current = true;
        connectionCoordinatorRef.current?.resume(`lifecycle:${reason}`);
        notifyUploadWindowAvailable();
        scheduleForegroundRefresh(reason);
      },
      onNetworkChange: (online, reason) => {
        lifecycleOnlineRef.current = online;
        connectionCoordinatorRef.current?.setNetworkOnline(online, `lifecycle:${reason}`);
        if (online) notifyUploadWindowAvailable();
        if (!online) {
          authenticatedSocketRef.current = null;
          pauseSocketOutbox();
          activeUploadXhrRef.current?.abort();
        }
      }
    });
    lifecycleForegroundRef.current = lifecycle.isForeground();
    appForegroundRef.current = lifecycle.isForeground();
    lifecycleOnlineRef.current = lifecycle.isOnline();
    notifyUploadWindowAvailable();

    const heartbeatId = window.setInterval(() => {
      if (!appForegroundRef.current || !lifecycleOnlineRef.current) return;
      if (!connectionCoordinatorRef.current?.isReady(socketRef.current)) return;
      void reportMobileDeviceConnection();
    }, 60000);

    return () => {
      recoveryGeneration += 1;
      if (refreshTimer) window.clearTimeout(refreshTimer);
      window.clearInterval(heartbeatId);
      lifecycle.stop();
    };
  }, [loadMe, notifyUploadWindowAvailable, pauseSocketOutbox, previewMode, reportMobileDeviceConnection, token]);

  useEffect(() => {
    if (previewMode || !token || !accountReady || !me?.id) return;
    const owner = mobileReliabilityOwner();
    if (!owner || socketOutboxRestoreKeyRef.current === owner) return;
    socketOutboxRestoreKeyRef.current = owner;
    let cancelled = false;

    void listPersistedSocketOutbox(owner).then(async (entries) => {
      if (cancelled) return;
      for (const stored of entries) {
        if (cancelled) return;
        if (stored.message.fromId && stored.message.fromId !== me.id) {
          await removePersistedSocketOutboxEntry(owner, stored.message.id);
          continue;
        }
        const existing = chatRef.current.byPeer[stored.peerId]?.find((message) => message.id === stored.message.id);
        const existingStatus = normalizeDeliveryStatus(existing?.status);
        if (existingStatus && !['sending', 'failed'].includes(existingStatus)) {
          await removePersistedSocketOutboxEntry(owner, stored.message.id);
          continue;
        }

        // The server may have accepted the packet immediately before Android/iOS froze
        // the WebView. Reconcile by the stable message id before sending it again.
        const authoritative = await findMessageAlreadyStored(stored.peerId, stored.message);
        if (cancelled) return;
        if (authoritative) {
          appendChatMessage(stored.peerId, authoritative, me.id);
          await removePersistedSocketOutboxEntry(owner, stored.message.id);
          continue;
        }

        const entry: PendingSocketSend = {
          peerId: stored.peerId,
          message: { ...stored.message, status: 'sending' },
          authRetryCount: 0,
          attemptCount: 0,
          createdAt: stored.createdAt || Date.now()
        };
        socketOutboxRef.current[entry.message.id] = entry;
        if (existing) {
          dispatch({ type: 'ack', peerId: stored.peerId, id: entry.message.id, status: 'sending', error: '' });
          cacheAck(stored.peerId, entry.message.id, { status: 'sending', error: '' });
        }
      }

      const socketClient = socketRef.current;
      if (!socketClient || cancelled) return;
      const coordinator = connectionCoordinatorRef.current;
      coordinator?.ensureReady('outbox-restored');
      if (coordinator?.isReady(socketClient)) flushSocketOutbox(socketClient);
    }).catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [accountReady, flushSocketOutbox, me?.id, mobileReliabilityOwner, previewMode, token]);

  async function ensureLocalKeypair(options: { publish?: boolean } = {}): Promise<KeyPairJwk> {
    if (keypair.myPrivJwk && keypair.myPubJwk) return keypair;
    const generated = generateKeypair();
    keypairRef.current = generated;
    setKeypair(generated);
    await storageRef.current?.set('keypair', generated);
    if (options.publish !== false && generated.myPubJwk && token) {
      await api.profileUpdate({ pubkey_jwk: generated.myPubJwk }).catch(() => undefined);
    }
    return generated;
  }

  async function ensurePeer(peerId: string): Promise<PeerRecord> {
    let peer = peersRef.current.find((item) => item.id === peerId);
    const missingDirectMessageKey = !!peer && peer.type !== 'group' && (!('pubkey_jwk' in peer) || !peer.pubkey_jwk);
    if ((!peer || missingDirectMessageKey) && !previewMode && token) {
      await refreshPeersRef.current().catch(() => undefined);
      peer = peersRef.current.find((item) => item.id === peerId);
    }
    if (peer) return peer;
    throw new Error(t('conversationUnavailable'));
  }

  const waitForSocketReady = useCallback(async (timeoutMs = 12000): Promise<SkyChatSocketClient> => {
    const coordinator = connectionCoordinatorRef.current;
    if (!coordinator) {
      throw new Error(t('chatNotConnected'));
    }
    try {
      return await coordinator.waitUntilReady(timeoutMs, 'consumer-wait');
    } catch {
      throw new Error(t('chatNotConnected'));
    }
  }, [t]);

  async function deliverSocketMessage(peerId: string, message: ChatMessage): Promise<void> {
    // Persist the encrypted packet before attempting transport delivery. A mobile OS can
    // suspend or terminate the WebView at any point after this await without losing it.
    await sendOrQueueSocketMessage(peerId, message);
  }

  function uploadWindowAvailable(): boolean {
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    return appForegroundRef.current && lifecycleOnlineRef.current && visible;
  }

  async function waitForUploadWindow(): Promise<void> {
    if (uploadWindowAvailable()) return;
    await new Promise<void>((resolve) => {
      const release = () => {
        uploadWindowWaitersRef.current.delete(release);
        resolve();
      };
      uploadWindowWaitersRef.current.add(release);
      // Avoid missing a foreground/network transition between the first check
      // and registering this waiter.
      if (uploadWindowAvailable()) release();
    });
  }

  function uploadEncryptedBlobOnce(
    blob: Blob,
    fileName: string,
    mimeType: string,
    onProgress: (percent: number) => void
  ): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      if (!token) {
        reject(new MobileUploadError(t('chatNotConnected'), false));
        return;
      }

      const xhr = new XMLHttpRequest();
      activeUploadXhrRef.current = xhr;
      const finish = () => {
        if (activeUploadXhrRef.current === xhr) activeUploadXhrRef.current = null;
      };
      xhr.open('POST', `${normalizeBaseUrl(config.apiBaseUrl)}/api/upload`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || event.total <= 0) return;
        onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
      };
      xhr.onerror = () => {
        finish();
        reject(new MobileUploadError(t('uploadFailed'), true));
      };
      xhr.onabort = () => {
        finish();
        reject(new MobileUploadError(
          uploadWindowAvailable()
            ? t('uploadFailed')
            : t('uploadPaused'),
          true
        ));
      };
      xhr.onload = () => {
        finish();
        const payload = (() => {
          try { return JSON.parse(String(xhr.responseText || '{}')) as Record<string, unknown>; }
          catch { return {}; }
        })();
        if (xhr.status < 200 || xhr.status >= 300 || payload.ok === false) {
          const retryable = xhr.status === 0 || xhr.status === 408 || xhr.status === 409 || xhr.status === 425 || xhr.status === 429 || xhr.status >= 500;
          reject(new MobileUploadError(String(payload.error || `${t('uploadFailed')} (${xhr.status})`), retryable));
          return;
        }
        resolve(payload);
      };

      const form = new FormData();
      form.append('file', blob, fileName);
      form.append('originalMimeType', mimeType || 'application/octet-stream');
      xhr.send(form);
    });
  }

  async function uploadEncryptedBlobWithProgress(
    blob: Blob,
    fileName: string,
    mimeType: string,
    onProgress: (percent: number) => void
  ): Promise<Record<string, unknown>> {
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt += 1) {
      await waitForUploadWindow();
      try {
        return await uploadEncryptedBlobOnce(blob, fileName, mimeType, onProgress);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(t('uploadFailed'));
        const retryable = !(error instanceof MobileUploadError) || error.retryable;
        if (!retryable || attempt >= UPLOAD_MAX_ATTEMPTS) throw lastError;
        await waitForUploadWindow();
        await new Promise((resolve) => window.setTimeout(resolve, Math.min(7000, UPLOAD_RETRY_BASE_DELAY_MS * (2 ** (attempt - 1)))));
      }
    }
    throw lastError || new Error(t('uploadFailed'));
  }

  async function login(id: string, password: string): Promise<void> {
    const passhash = await sha256Hex(password);
    const res = await api.login({ id, passhash });
    const nextToken = String(res.token || '');
    if (!nextToken) throw new Error(res.error || 'Login failed');
    const encryptedBackup = res.encrypted_private_key as Record<string, unknown> | string | undefined;
    const serverPrivateHash = String(res.private_key_hash || '');
    if (encryptedBackup) {
      let needsRestore = !keypair.myPrivJwk;
      if (keypair.myPrivJwk && serverPrivateHash) {
        const localHash = await calculatePrivateKeyHash(keypair.myPrivJwk);
        needsRestore = localHash !== serverPrivateHash;
      }
      if (needsRestore) {
        const parsedBackup = typeof encryptedBackup === 'string' ? JSON.parse(encryptedBackup) : encryptedBackup;
        const restoredPrivate = await importPrivateKeyBackup(parsedBackup, passhash);
        if (serverPrivateHash) {
          const restoredHash = await calculatePrivateKeyHash(restoredPrivate);
          if (restoredHash !== serverPrivateHash) throw new Error('Key backup verification failed');
        }
        const restored = keypairFromPrivate(restoredPrivate);
        setKeypair(restored);
        await storageRef.current?.set('keypair', restored);
      }
    }
    await storageRef.current?.set('authToken', nextToken);
    const profile = profileFromResult(res as Record<string, unknown>);
    if (profile?.id) replaceMe(profile);
    setToken(nextToken);
  }

  async function register(payload: { nickname: string; password: string; inviteCode: string }): Promise<void> {
    const localKeys = await ensureLocalKeypair({ publish: false });
    if (!localKeys.myPrivJwk || !localKeys.myPubJwk) throw new Error('Key generation failed');
    const passhash = await sha256Hex(payload.password);
    const encryptedBackup = await exportPrivateKeyBackup(localKeys.myPrivJwk, passhash);
    const privateKeyHash = await calculatePrivateKeyHash(localKeys.myPrivJwk);
    const res = await api.register({
      nickname: payload.nickname,
      passhash,
      inviteCode: payload.inviteCode,
      pubkey_jwk: localKeys.myPubJwk,
      encrypted_private_key: JSON.stringify(encryptedBackup),
      private_key_hash: privateKeyHash
    });
    const nextToken = String(res.token || '');
    if (!nextToken) throw new Error(res.error || 'Registration failed');
    await storageRef.current?.set('keypair', localKeys);
    await storageRef.current?.set('authToken', nextToken);
    const profile = profileFromResult(res as Record<string, unknown>);
    if (profile?.id) replaceMe(profile);
    setToken(nextToken);
  }

  async function logout(): Promise<void> {
    const owner = mobileReliabilityOwner();
    if (!previewMode && token) {
      reportMobileDeviceDisconnection(token);
      await disableWebPush(api).then(setWebPushState).catch(() => undefined);
    }
    if (owner) await clearMobileReliabilityState(owner);
    await clearLocalAuthSession();
  }

  async function openPeer(peerId: string): Promise<void> {
    if (activeChatViewRef.current === peerId) {
      dispatch({ type: 'open-peer', peerId });
    }
    if (previewMode) return;
    if (!token) return;
    await loadLatestPeerPage(peerId, { render: true, markRead: true });
  }

  function setPeerViewActive(peerId: string, active: boolean): void {
    if (!peerId) return;
    if (active) {
      activeChatViewRef.current = peerId;
      dispatch({ type: 'open-peer', peerId });
      return;
    }
    if (activeChatViewRef.current === peerId) {
      activeChatViewRef.current = null;
      dispatch({ type: 'open-peer', peerId: '' });
    }
  }

  async function findMessageAlreadyStored(peerId: string, message: ChatMessage): Promise<ChatMessage | null> {
    if (!peerId || !message.id || previewMode || !token) return null;
    try {
      const res = await api.messagesSync({
        peerId,
        lastTs: Math.max(0, Number(message.ts || 0) - 5 * 60 * 1000),
        lastSeq: 0
      });
      if (res.ok === false) return null;
      const stored = syncedMessagesFromResult(res as Record<string, unknown>)
        .map(normalizeChatMessageForClient)
        .find((candidate) => candidate.id === message.id);
      return stored || null;
    } catch {
      return null;
    }
  }

  async function retryMessage(peerId: string, message: ChatMessage): Promise<void> {
    if (!message?.id || !peerId) throw new Error(t('retryNotAvailable'));
    const currentMe = meRef.current;
    if (!previewMode && (!currentMe?.id || message.fromId !== currentMe.id)) {
      throw new Error(t('retryNotAvailable'));
    }

    if (previewMode) {
      dispatch({ type: 'ack', peerId, id: message.id, status: 'delivered' });
      cacheAck(peerId, message.id, { status: 'delivered', error: '' });
      return;
    }

    const uploadSource = chatMessageMeta(message).source;
    let pendingUploadFile = pendingUploadFilesRef.current[message.id];
    if (!pendingUploadFile) {
      const owner = mobileReliabilityOwner();
      if (owner) {
        const restored = await restorePendingUpload(owner, message.id);
        pendingUploadFile = restored?.file;
        if (pendingUploadFile) pendingUploadFilesRef.current[message.id] = pendingUploadFile;
      }
    }
    if (uploadSource === 'server-upload') {
      const storedUpload = await findMessageAlreadyStored(peerId, message);
      if (storedUpload) {
        clearPendingSendTimer(message.id);
        appendChatMessage(peerId, storedUpload, currentMe!.id);
        delete pendingUploadFilesRef.current[message.id];
        const owner = mobileReliabilityOwner();
        if (owner) void removePendingUpload(owner, message.id);
        return;
      }
      const uploadedFileId = String(message.file?.id || '');
      const uploadAlreadyCompleted = !!uploadedFileId
        && !uploadedFileId.startsWith('upload-pending-')
        && !uploadedFileId.startsWith('p2p-pending-')
        && !!message.iv;
      if (uploadAlreadyCompleted) {
        const outbound = { ...message, status: 'sending', error: '', uploadProgress: 100 } as ChatMessage;
        dispatch({ type: 'ack', peerId, id: message.id, status: 'sending', error: '' });
        cacheAck(peerId, message.id, { status: 'sending', error: '' });
        await deliverSocketMessage(peerId, outbound);
        return;
      }
      if (!pendingUploadFile) {
        throw new Error(t('retryNotAvailable'));
      }
      const peer = await ensurePeer(peerId);
      const localPreviewUrl = String(message.file?.url || '').startsWith('blob:') ? String(message.file?.url || '') : '';
      dispatch({ type: 'ack', peerId, id: message.id, status: 'sending', error: '' });
      cacheAck(peerId, message.id, { status: 'sending', error: '' });
      void enqueueServerFileUpload(peerId, peer, currentMe!, pendingUploadFile, {
        ...message,
        status: 'sending',
        uploadProgress: 0,
        meta: { ...chatMessageMeta(message), source: 'server-upload', phase: 'queued', progress: 0 }
      } as ChatMessage & Record<string, unknown>, localPreviewUrl);
      return;
    }

    if (isChatP2PFileMessage(message)) {
      if (!pendingUploadFile) {
        throw new Error(t('retryNotAvailable'));
      }
      const peer = await ensurePeer(peerId);
      const retryPlaceholder = { ...message, status: 'sending', error: '', uploadProgress: 0 } as ChatMessage;
      dispatch({ type: 'ack', peerId, id: message.id, status: 'sending', error: '' });
      cacheAck(peerId, message.id, { status: 'sending', error: '' });
      await enqueueP2PFileTransfer(peerId, peer, currentMe!, pendingUploadFile, retryPlaceholder);
      return;
    }

    const peer = await ensurePeer(peerId);
    const stored = await findMessageAlreadyStored(peerId, message);
    if (stored) {
      clearPendingSendTimer(message.id);
      appendChatMessage(peerId, stored, currentMe!.id);
      return;
    }

    const base: ChatMessage = {
      id: message.id,
      fromId: currentMe!.id,
      toId: peerId,
      ts: Number(message.ts || Date.now()),
      type: message.type,
      plain: message.plain,
      replyTo: message.replyTo,
      iv: message.iv,
      payload: message.payload,
      file: message.file,
      status: 'sending',
      meta: (message as ChatMessage & { meta?: unknown }).meta
    };

    let outbound: ChatMessage = base;
    if (message.type === 'text') {
      const text = String(message.plain || '');
      if (!text) throw new Error(t('retryNotAvailable'));
      const localKeys = await ensureLocalKeypair();
      if (isGroup(peer)) {
        const enc = await encryptTextForGroup(peerId, text);
        outbound = { ...base, iv: enc.iv, payload: enc.data };
      } else if (peer && 'pubkey_jwk' in peer && peer.pubkey_jwk && localKeys.myPrivJwk) {
        const enc = await encryptText(localKeys.myPrivJwk, peerId, peer.pubkey_jwk, text);
        outbound = encryptedDirectOutbound(base, enc.iv, enc.data);
      } else {
        throw new Error(t('missingPeerKey'));
      }
    } else if (message.type === 'gif') {
      if (!message.payload) throw new Error(t('retryNotAvailable'));
    } else if (['file', 'image', 'video', 'audio'].includes(String(message.type || ''))) {
      if (!message.file?.id && !message.file?.url) {
        throw new Error(t('retryNotAvailable'));
      }
    } else {
      throw new Error(t('retryNotAvailable'));
    }

    appendChatMessage(peerId, { ...base, error: '' } as ChatMessage & { error?: string }, currentMe!.id);
    dispatch({ type: 'ack', peerId, id: message.id, status: 'sending', error: '' });
    cacheAck(peerId, message.id, { status: 'sending', error: '' });
    await deliverSocketMessage(peerId, outbound);
  }

  async function sendText(peerId: string, text: string, replyTo?: ChatMessage | null): Promise<void> {
    const replyInfo = replyTo ? {
      id: replyTo.id,
      fromId: replyTo.fromId,
      type: replyTo.type,
      preview: replyTo.plain || replyTo.file?.name || replyTo.type
    } : undefined;
    if (previewMode) {
      appendChatMessage(
        peerId,
        {
          id: crypto.randomUUID(),
          fromId: PREVIEW_ME.id,
          toId: peerId,
          ts: Date.now(),
          type: 'text',
          plain: text,
          replyTo: replyInfo,
          status: 'delivered'
        },
        PREVIEW_ME.id
      );
      return;
    }
    const peer = await ensurePeer(peerId);
    const currentMe = meRef.current;
    if (!currentMe?.id) throw new Error(t('chatStillLoading'));
    const localKeys = await ensureLocalKeypair();
    const base: ChatMessage = {
      id: crypto.randomUUID(),
      fromId: currentMe.id,
      toId: peerId,
      ts: Date.now(),
      type: 'text',
      plain: text,
      replyTo: replyInfo,
      status: 'sending'
    };

    let outbound = base;
    try {
      if (isGroup(peer)) {
        const enc = await encryptTextForGroup(peerId, text);
        outbound = { ...base, iv: enc.iv, payload: enc.data };
      } else if (peer && 'pubkey_jwk' in peer && peer.pubkey_jwk && localKeys.myPrivJwk) {
        const enc = await encryptText(localKeys.myPrivJwk, peerId, peer.pubkey_jwk, text);
        outbound = encryptedDirectOutbound(base, enc.iv, enc.data);
      } else {
        throw new Error(t('missingPeerKey'));
      }
    } catch (error) {
      throw error instanceof Error ? error : new Error(t('encryptionFailed'));
    }

    appendChatMessage(peerId, base, currentMe.id);
    await deliverSocketMessage(peerId, outbound);
  }

  async function sendGif(peerId: string, url: string): Promise<void> {
    if (!previewMode) await ensurePeer(peerId);
    const currentMe = meRef.current;
    if (!previewMode && !currentMe?.id) throw new Error(t('chatStillLoading'));
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      fromId: currentMe?.id || PREVIEW_ME.id,
      toId: peerId,
      ts: Date.now(),
      type: 'gif',
      plain: '[GIF]',
      payload: { url },
      status: previewMode ? 'delivered' : 'sending'
    };
    appendChatMessage(peerId, message, me?.id || PREVIEW_ME.id);
    if (!previewMode) {
      await deliverSocketMessage(peerId, message);
    }
  }

  async function invokeBot(peerId: string, bot: BotDefinition, command: string, args: string[] = [], rawText = ''): Promise<void> {
    if (!bot?.id) throw new Error('Bot is not available');
    const currentMe = meRef.current;
    if (!currentMe?.id && !previewMode) throw new Error(t('chatStillLoading'));
    if (!previewMode) await ensurePeer(peerId);
    const socketClient = previewMode ? null : await waitForSocketReady();
    const requestId = `bot_${globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;
    const normalizedCommand = String(command || '').trim().toLowerCase();
    const message: ChatMessage = {
      id: requestId,
      fromId: currentMe?.id || PREVIEW_ME.id,
      toId: peerId,
      ts: Date.now(),
      type: 'bot_media',
      plain: `${bot.name || bot.id} 正在获取...`,
      payload: { mediaType: normalizedCommand === 'gif' ? 'gif' : 'image' },
      status: previewMode ? 'delivered' : 'pending',
      meta: {
        isBot: true,
        botId: bot.id,
        botName: bot.name || bot.id,
        botAvatarUrl: bot.avatarUrl || '',
        command: normalizedCommand,
        args,
        requestId,
        phase: previewMode ? 'complete' : 'pending'
      }
    };

    appendChatMessage(peerId, message, currentMe?.id || PREVIEW_ME.id);
    if (previewMode) return;

    socketClient!.emit('bot:invoke', {
      requestId,
      peerId,
      botId: bot.id,
      command: normalizedCommand,
      args,
      rawText
    }, (ack = {}) => {
      const result = ack as { ok?: boolean; error?: string };
      if (result.ok !== false) return;
      appendChatMessage(peerId, {
        ...message,
        status: 'failed',
        plain: tRef.current('botFetchFailed', '获取失败'),
        meta: {
          ...(message.meta && typeof message.meta === 'object' ? message.meta as Record<string, unknown> : {}),
          phase: 'failed',
          error: result.error || tRef.current('botFetchFailed', '获取失败')
        }
      }, currentMe?.id || PREVIEW_ME.id);
    });
  }

  function messageTypeForFile(file: File): ChatMessage['type'] {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'file';
  }

  async function encryptFileForPeer(peerId: string, peer: PeerRecord, file: File): Promise<{ iv: string; data: Blob }> {
    const localKeys = await ensureLocalKeypair();
    const rawBuffer = await file.arrayBuffer();
    if (isGroup(peer)) return encryptArrayBufferForGroup(peerId, rawBuffer);
    if (peer && 'pubkey_jwk' in peer && peer.pubkey_jwk && localKeys.myPrivJwk) {
      return encryptArrayBuffer(localKeys.myPrivJwk, peerId, peer.pubkey_jwk, rawBuffer);
    }
    throw new Error(t('missingPeerKey'));
  }

  async function runP2PFileTransfer(
    peerId: string,
    peer: PeerRecord,
    currentMe: UserProfile,
    file: File,
    placeholder: ChatMessage
  ): Promise<void> {
    try {
      await waitForUploadWindow();
      await waitForSocketReady();
      const encrypted = await encryptFileForPeer(peerId, peer, file);
      const message = { ...placeholder, iv: encrypted.iv, status: 'sending' } as ChatMessage;
      appendChatMessage(peerId, message, currentMe.id);
      await getChatP2PFileManager().send(peerId, file, encrypted.data, encrypted.iv, message);
      // Keep the source file until the chat message receives a server acknowledgement.
      // This allows a suspended mobile WebView to retry the transfer safely.
    } catch (error) {
      const failure = error instanceof Error ? error.message : t('uploadFailed');
      patchChatP2PMessage(peerId, placeholder.id, {
        status: 'failed',
        uploadProgress: 0,
        error: failure,
        meta: { phase: 'failed', error: failure, totalBytes: file.size }
      } as Partial<ChatMessage> & Record<string, unknown>);
    }
  }

  function enqueueP2PFileTransfer(
    peerId: string,
    peer: PeerRecord,
    currentMe: UserProfile,
    file: File,
    placeholder: ChatMessage
  ): Promise<void> {
    if (queuedUploadMessageIdsRef.current.has(placeholder.id)) return uploadQueueTailRef.current;
    queuedUploadMessageIdsRef.current.add(placeholder.id);
    const task = uploadQueueTailRef.current
      .catch(() => undefined)
      .then(() => runP2PFileTransfer(peerId, peer, currentMe, file, placeholder))
      .finally(() => queuedUploadMessageIdsRef.current.delete(placeholder.id));
    uploadQueueTailRef.current = task.catch(() => undefined);
    return task;
  }

  async function runServerFileUpload(
    peerId: string,
    peer: PeerRecord,
    currentMe: UserProfile,
    file: File,
    placeholder: ChatMessage,
    localPreviewUrl: string
  ): Promise<void> {
    pendingUploadFilesRef.current[placeholder.id] = file;
    try {
      patchChatP2PMessage(peerId, placeholder.id, {
        status: 'sending',
        uploadProgress: 2,
        error: '',
        meta: { source: 'server-upload', phase: 'encrypting', progress: 2, totalBytes: file.size }
      } as Partial<ChatMessage> & Record<string, unknown>);

      await waitForUploadWindow();
      const encrypted = await encryptFileForPeer(peerId, peer, file);
      patchChatP2PMessage(peerId, placeholder.id, {
        uploadProgress: 12,
        iv: encrypted.iv,
        meta: { phase: 'uploading', progress: 12 }
      } as Partial<ChatMessage> & Record<string, unknown>);

      const upload = await uploadEncryptedBlobWithProgress(encrypted.data, file.name, file.type, (rawPercent) => {
        const percent = 12 + Math.round(rawPercent * 0.82);
        patchChatP2PMessage(peerId, placeholder.id, {
          uploadProgress: percent,
          meta: {
            phase: 'uploading',
            progress: percent,
            bytesTransferred: Math.round((file.size * rawPercent) / 100),
            totalBytes: file.size
          }
        } as Partial<ChatMessage> & Record<string, unknown>);
      });

      const fileInfo = normalizeMessageFileInfo({
        id: upload.fileId || (upload.file as Record<string, unknown> | undefined)?.id || (upload.data as Record<string, unknown> | undefined)?.id,
        name: file.name,
        size: file.size,
        mime: file.type
      });
      const finalFile = {
        id: fileInfo?.id,
        name: file.name,
        size: file.size,
        type: file.type,
        mime: file.type,
        url: fileInfo?.url
      };
      const outbound: ChatMessage = {
        ...placeholder,
        iv: encrypted.iv,
        file: finalFile,
        plain: file.name,
        status: 'sending',
        uploadProgress: 100,
        meta: {
          source: 'server-upload',
          phase: 'sending',
          progress: 100,
          bytesTransferred: file.size,
          totalBytes: file.size
        }
      } as ChatMessage & Record<string, unknown>;

      patchChatP2PMessage(peerId, placeholder.id, outbound as Partial<ChatMessage> & Record<string, unknown>);
      if (localPreviewUrl) {
        window.setTimeout(() => {
          try { URL.revokeObjectURL(localPreviewUrl); } catch { }
        }, 2500);
      }
      await deliverSocketMessage(peerId, outbound);
      delete pendingUploadFilesRef.current[placeholder.id];
      const owner = mobileReliabilityOwner();
      if (owner) await removePendingUpload(owner, placeholder.id);
    } catch (error) {
      const failure = error instanceof Error ? error.message : t('uploadFailed');
      patchChatP2PMessage(peerId, placeholder.id, {
        status: 'failed',
        error: failure,
        meta: { source: 'server-upload', phase: 'failed', error: failure }
      } as Partial<ChatMessage> & Record<string, unknown>);
    }
  }

  function enqueueServerFileUpload(
    peerId: string,
    peer: PeerRecord,
    currentMe: UserProfile,
    file: File,
    placeholder: ChatMessage,
    localPreviewUrl: string
  ): Promise<void> {
    if (queuedUploadMessageIdsRef.current.has(placeholder.id)) return uploadQueueTailRef.current;
    queuedUploadMessageIdsRef.current.add(placeholder.id);
    const task = uploadQueueTailRef.current
      .catch(() => undefined)
      .then(() => runServerFileUpload(peerId, peer, currentMe, file, placeholder, localPreviewUrl))
      .finally(() => {
        queuedUploadMessageIdsRef.current.delete(placeholder.id);
      });
    uploadQueueTailRef.current = task.catch(() => undefined);
    return task;
  }

  useEffect(() => {
    if (previewMode || !token || !accountReady || !me?.id) return;
    const owner = mobileReliabilityOwner();
    if (!owner || uploadRestoreKeyRef.current === owner) return;
    uploadRestoreKeyRef.current = owner;
    let cancelled = false;

    void listPendingUploads(owner).then(async (uploads) => {
      for (const upload of uploads) {
        if (cancelled) return;
        const existing = chatRef.current.byPeer[upload.peerId]?.find((message) => message.id === upload.messageId);
        const recoveryProbe = existing || ({
          id: upload.messageId,
          fromId: me.id,
          toId: upload.peerId,
          ts: upload.createdAt,
          type: upload.mode === 'p2p' ? 'file' : messageTypeForFile(upload.file),
          plain: upload.file.name,
          status: 'sending'
        } as ChatMessage);
        const status = normalizeDeliveryStatus(existing?.status);
        if (status && !['sending', 'failed'].includes(status)) {
          await removePendingUpload(owner, upload.messageId);
          continue;
        }
        const stored = await findMessageAlreadyStored(upload.peerId, recoveryProbe);
        if (stored) {
          appendChatMessage(upload.peerId, stored, me.id);
          await removePendingUpload(owner, upload.messageId);
          continue;
        }
        const peer = await ensurePeer(upload.peerId).catch(() => null);
        const currentMe = meRef.current;
        if (!peer || !currentMe?.id) continue;
        pendingUploadFilesRef.current[upload.messageId] = upload.file;
        const localPreviewUrl = /^(image|video|audio)\//i.test(upload.file.type) ? URL.createObjectURL(upload.file) : '';
        const placeholder = existing || {
          id: upload.messageId,
          fromId: currentMe.id,
          toId: upload.peerId,
          ts: upload.createdAt,
          type: upload.mode === 'p2p' ? 'file' : messageTypeForFile(upload.file),
          payload: null,
          plain: upload.file.name,
          file: {
            id: `${upload.mode === 'p2p' ? 'p2p' : 'upload'}-pending-${upload.messageId}`,
            name: upload.file.name,
            size: upload.file.size,
            type: upload.file.type,
            mime: upload.file.type,
            url: localPreviewUrl
          },
          status: 'sending',
          uploadProgress: 0,
          meta: upload.mode === 'p2p' ? {
            source: 'chat-p2p-file',
            p2p: true,
            transfer: 'chat-p2p-file',
            direction: 'outgoing',
            phase: 'waiting',
            speedBps: 0,
            bytesTransferred: 0,
            totalBytes: upload.file.size
          } : {
            source: 'server-upload',
            direction: 'outgoing',
            phase: 'queued',
            progress: 0,
            bytesTransferred: 0,
            totalBytes: upload.file.size
          }
        } as ChatMessage & Record<string, unknown>;
        appendChatMessage(upload.peerId, { ...placeholder, status: 'sending' }, currentMe.id);
        if (upload.mode === 'p2p' || (existing && isChatP2PFileMessage(existing))) {
          void enqueueP2PFileTransfer(upload.peerId, peer, currentMe, upload.file, placeholder);
        } else {
          void enqueueServerFileUpload(upload.peerId, peer, currentMe, upload.file, placeholder, localPreviewUrl);
        }
      }
    }).catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [accountReady, me?.id, mobileReliabilityOwner, previewMode, token]);

  async function sendFile(peerId: string, file: File): Promise<void> {
    if (previewMode) {
      appendChatMessage(
        peerId,
        {
          id: crypto.randomUUID(),
          fromId: PREVIEW_ME.id,
          toId: peerId,
          ts: Date.now(),
          type: messageTypeForFile(file),
          plain: file.name,
          file: { name: file.name, size: file.size, type: file.type },
          status: 'delivered'
        },
        PREVIEW_ME.id
      );
      return;
    }

    const peer = await ensurePeer(peerId);
    const currentMe = meRef.current;
    if (!currentMe?.id) throw new Error(t('chatStillLoading'));

    const useChatP2P = shouldUseChatP2PFile(peer, file);
    const messageId = crypto.randomUUID();
    const mediaType = messageTypeForFile(file);
    const localPreviewUrl = /^(image|video|audio)\//i.test(file.type) ? URL.createObjectURL(file) : '';
    const placeholder: ChatMessage = {
      id: messageId,
      fromId: currentMe.id,
      toId: peerId,
      ts: Date.now(),
      type: useChatP2P ? 'file' : mediaType,
      payload: null,
      file: {
        id: `${useChatP2P ? 'p2p' : 'upload'}-pending-${messageId}`,
        name: file.name,
        size: file.size,
        type: file.type,
        mime: file.type,
        url: localPreviewUrl
      },
      plain: file.name,
      uploadProgress: 0,
      meta: useChatP2P ? {
        source: 'chat-p2p-file',
        p2p: true,
        transfer: 'chat-p2p-file',
        direction: 'outgoing',
        phase: 'waiting',
        speedBps: 0,
        bytesTransferred: 0,
        totalBytes: file.size
      } : {
        source: 'server-upload',
        direction: 'outgoing',
        phase: 'queued',
        progress: 0,
        bytesTransferred: 0,
        totalBytes: file.size
      },
      status: 'sending'
    } as ChatMessage & Record<string, unknown>;

    appendChatMessage(peerId, placeholder, currentMe.id);
    pendingUploadFilesRef.current[messageId] = file;
    const reliabilityOwner = mobileReliabilityOwner();
    if (reliabilityOwner) await persistPendingUpload(reliabilityOwner, messageId, peerId, file, useChatP2P ? 'p2p' : 'server');

    if (useChatP2P) {
      await enqueueP2PFileTransfer(peerId, peer, currentMe, file, placeholder);
      return;
    }

    await enqueueServerFileUpload(peerId, peer, currentMe, file, placeholder, localPreviewUrl);
  }

  async function cancelChatP2PFile(message: ChatMessage): Promise<boolean> {
    if (!isChatP2PFileMessage(message)) return false;
    return getChatP2PFileManager().cancel(message);
  }

  async function editMessage(peerId: string, messageId: string, text: string): Promise<void> {
    const nextText = text.trim();
    if (!nextText) throw new Error('Message cannot be empty');
    const peer = peers.find((item) => item.id === peerId);
    const existing = chat.byPeer[peerId]?.find((message) => message.id === messageId);
    if (!existing || existing.fromId !== (me?.id || PREVIEW_ME.id) || existing.type !== 'text') {
      throw new Error('This message cannot be edited');
    }
    const editedTs = Date.now();

    if (previewMode) {
      dispatch({ type: 'edit', peerId, id: messageId, plain: nextText, editedTs });
      cacheEdit(peerId, messageId, { plain: nextText, editedTs });
      return;
    }

    const localKeys = await ensureLocalKeypair();
    let encrypted: { iv: string; data: string };
    if (isGroup(peer)) {
      encrypted = await encryptTextForGroup(peerId, nextText);
    } else if (peer && 'pubkey_jwk' in peer && peer.pubkey_jwk && localKeys.myPrivJwk) {
      encrypted = await encryptText(localKeys.myPrivJwk, peerId, peer.pubkey_jwk, nextText);
    } else {
      throw new Error('Missing peer key for encrypted edit');
    }

    dispatch({ type: 'edit', peerId, id: messageId, plain: nextText, iv: encrypted.iv, payload: encrypted.data, editedTs });
    cacheEdit(peerId, messageId, { plain: nextText, iv: encrypted.iv, payload: encrypted.data, editedTs });
    socketRef.current?.emit('message:edit', { id: messageId, toId: peerId, iv: encrypted.iv, payload: encrypted.data, editedTs });
  }

  async function deleteMessage(peerId: string, messageId: string): Promise<void> {
    const existing = chat.byPeer[peerId]?.find((message) => message.id === messageId);
    if (!existing || existing.fromId !== (me?.id || PREVIEW_ME.id)) {
      throw new Error('This message cannot be deleted');
    }
    dispatch({ type: 'delete', peerId, id: messageId });
    cacheDelete(peerId, messageId);
    socketRef.current?.emit('message:delete', { id: messageId, toId: peerId });
  }

  async function clearPeer(peerId: string): Promise<void> {
    invalidateLocalSearchIndex(peerId);
    dispatch({ type: 'hydrate', peerId, messages: [] });
    replacePeers((current) => current.map((peer) => {
      if (peer.id !== peerId) return peer;
      const next = { ...peer } as PeerRecord & Record<string, unknown>;
      delete next.lastMessageText;
      delete next.lastMessageTime;
      return next;
    }));
    await ensureChatRepository()?.clear(peerId).catch(() => undefined);
    socketRef.current?.emit('chat:clear_local', { peerId });
  }


  async function sendFriendRequest(toId: string): Promise<void> {
    await sendMobileFriendRequest(toId, {
      t,
      selfId: String(meRef.current?.id || '').trim(),
      previewMode,
      getPeers: () => peersRef.current,
      replacePeers,
      search: (query) => api.search(query),
      waitForSocketReady,
      refreshPeers: () => refreshPeersRef.current(),
    });
  }

  async function respondFriendRequest(fromId: string, accept: boolean): Promise<void> {
    const target = String(fromId || '').trim();
    if (!target) throw new Error(t('friendIdRequired'));
    if (previewMode) {
      setPeers((current) => accept
        ? current.map((peer) => peer.id === target ? { ...peer, status: 'accepted' } as PeerRecord : peer)
        : current.filter((peer) => peer.id !== target));
      return;
    }
    const socketClient = await waitForSocketReady();
    socketClient.emit('friend:respond', { fromId: target, accept });
    window.setTimeout(() => { void refreshPeersRef.current().catch(() => undefined); }, 450);
  }

  function markRead(peerId: string): void {
    dispatch({ type: 'mark-read', peerId });
    const previousActiveView = activeChatViewRef.current;
    activeChatViewRef.current = peerId;
    const maxReadTs = emitReadReceipts(peerId, chat.byPeer[peerId] || []);
    activeChatViewRef.current = previousActiveView;
    if (maxReadTs <= 0) socketRef.current?.markRead(peerId, Date.now());
  }

  function markUnread(peerId: string): void {
    if (!peerId) return;
    dispatch({ type: 'mark-unread', peerId });
  }

  async function updateContactSettings(peerId: string, patch: Partial<ContactSettings>): Promise<ContactSettings | null> {
    const target = String(peerId || '').trim();
    if (!target) throw new Error(t('conversationUnavailable'));
    if (previewMode) {
      const current = peersRef.current.find((peer) => peer.id === target);
      const next = {
        ...(current?.contactSettings || {}),
        ...patch,
        peerId: target,
        updatedAt: Date.now()
      } as ContactSettings;
      patchContactSettingsRef.current(target, next);
      return next;
    }
    api.setToken(token);
    const res = await api.updateContactSettings(target, patch);
    if (res.ok === false) throw new Error(String(res.error || t('saveFailed')));
    const settings = (res.settings || ((res.data as { settings?: ContactSettings } | undefined)?.settings)) as ContactSettings | undefined;
    if (settings?.peerId) {
      patchContactSettingsRef.current(settings.peerId, settings);
      return settings;
    }
    await refreshPeersRef.current().catch(() => undefined);
    return null;
  }

  function emitTyping(peerId: string, on: boolean): void {
    if (!peerId || previewMode) return;
    const socketClient = socketRef.current;
    if (socketClient && socketIsReady(socketClient)) {
      delete pendingTypingRef.current[peerId];
      emitTypingThroughSocket(socketClient, peerId, on);
      return;
    }

    if (on) pendingTypingRef.current[peerId] = true;
    else delete pendingTypingRef.current[peerId];
    connectionCoordinatorRef.current?.ensureReady('typing');
  }

  async function updateWeatherCity(city: string): Promise<void> {
    const requested = city.trim();
    if (!requested) throw new Error('City is required');
    if (previewMode) {
      const weather = { ...(me?.weather || {}), cityName: requested, showWeather: me?.show_weather !== false };
      await applySelfWeatherUpdate(weather);
      return;
    }
    const res = await api.weatherUpdate({ city: requested });
    if (res.ok === false) throw new Error(res.error || 'Weather update failed');
    await applySelfWeatherUpdate(res.weather || (res.data as Record<string, unknown> | undefined)?.weather);
  }

  async function updateWeatherVisibility(enabled: boolean): Promise<void> {
    if (previewMode) {
      await applySelfWeatherUpdate({ ...(me?.weather || {}), showWeather: enabled });
      return;
    }
    const res = await api.weatherUpdate({ showWeather: enabled });
    if (res.ok === false) throw new Error(res.error || 'Weather update failed');
    await applySelfWeatherUpdate(res.weather || (res.data as Record<string, unknown> | undefined)?.weather);
  }

  async function updatePreferences(patch: Partial<UserPreferences> & Partial<MobilePreferences> & Record<string, unknown>): Promise<void> {
    const next = normalizePreferences({ ...preferences, ...patch });
    setPreferences(next);
    await storageRef.current?.set('preferences', next);
    if (token && !previewMode) {
      await api.updateUserConfig(next).catch(() => undefined);
    }
  }

  async function enableWebPushNotifications(): Promise<void> {
    if (previewMode) {
      await updatePreferences({ webpushEnabled: true });
      setWebPushState({ supported: true, permission: 'granted', subscriptionActive: true });
      return;
    }

    await syncServiceWorkerState({
      keypair,
      peers,
      locale: preferences.locale,
      previewsEnabled: preferences.previewsEnabled
    });

    const next = await enableWebPush(api, { requestPermission: true });
    setWebPushState(next);

    if (!next.supported) {
      await updatePreferences({ webpushEnabled: false });
      throw new Error(t('notificationsUnsupported'));
    }
    if (next.permission !== 'granted') {
      await updatePreferences({ webpushEnabled: false });
      throw new Error(next.permission === 'denied'
        ? t('notificationsPermissionDenied')
        : t('notificationsPermissionRequired'));
    }
    if (!next.subscriptionActive) {
      await updatePreferences({ webpushEnabled: false });
      throw new Error(t('notificationsEnableFailed'));
    }

    await updatePreferences({ webpushEnabled: true });
  }

  async function disableWebPushNotifications(): Promise<void> {
    if (previewMode) {
      await updatePreferences({ webpushEnabled: false });
      setWebPushState({ supported: true, permission: 'granted', subscriptionActive: false });
      return;
    }

    const next = await disableWebPush(api);
    setWebPushState(next);
    await updatePreferences({ webpushEnabled: false });
  }

  async function updateProfile(payload: Record<string, unknown>): Promise<void> {
    if (previewMode) {
      patchSelfProfile(payload as Partial<UserProfile>);
      return;
    }

    const previousProfile = meRef.current;
    let optimisticProfile = payload as Partial<UserProfile>;
    if (hasPresencePayload(payload)) {
      const optimisticPresence = presencePatchFromSocketPayload(payload, preferredPresenceRef.current);
      if (optimisticPresence.presence) {
        preferredPresenceRef.current = normalizeConfiguredPresence(optimisticPresence.presence, preferredPresenceRef.current);
      }
      optimisticProfile = {
        ...payload,
        ...optimisticPresence,
        ...(socketIsReady() ? { online: true } : {})
      } as Partial<UserProfile>;
      const receivedAt = Date.now();
      lastSelfPresencePatchRef.current = {
        patch: { ...(lastSelfPresencePatchRef.current?.patch || {}), ...optimisticPresence },
        receivedAt,
        revision: receivedAt
      };
    }
    patchSelfProfile(optimisticProfile);

    try {
      const res = await api.profileUpdate(payload);
      if (res.ok === false) throw new Error(res.error || 'Profile update failed');
      const profile = (res.profile || res.user || res.me || payload) as UserProfile;
      let nextProfile: Partial<UserProfile> = profile;
      if (hasPresencePayload(profile as Record<string, unknown>)) {
        const presencePatch = presencePatchFromSocketPayload(profile as Record<string, unknown>, preferredPresenceRef.current);
        if (presencePatch.presence) {
          preferredPresenceRef.current = normalizeConfiguredPresence(presencePatch.presence, preferredPresenceRef.current);
        }
        const receivedAt = Date.now();
        const revision = socketPresenceRevision(profile as Record<string, unknown>);
        const nextPatch = { ...(lastSelfPresencePatchRef.current?.patch || {}), ...presencePatch };
        lastSelfPresencePatchRef.current = { patch: nextPatch, receivedAt, revision };
        nextProfile = {
          ...profile,
          ...nextPatch,
          ...(socketIsReady() ? { online: true } : {})
        } as Partial<UserProfile>;
      }
      patchSelfProfile(nextProfile);
      pendingProfileBroadcastRef.current = nextProfile as Record<string, unknown>;
      const socketClient = socketRef.current;
      if (socketClient && socketIsReady(socketClient)) {
        socketClient.emit('profile:broadcast', { profile: nextProfile });
        pendingProfileBroadcastRef.current = null;
      }
    } catch (error) {
      if (previousProfile) replaceMe(previousProfile);
      throw error;
    }
  }

  async function updateProfileTimeZone(timeZoneId: string, displayName: string): Promise<void> {
    if (previewMode) {
      const previewTimeZone: UserTimeZone = {
        mode: 'manual',
        id: timeZoneId,
        displayName,
        offsetMinutes: 0,
        updatedAt: Date.now()
      };
      patchSelfProfile(profileTimeZonePatch(previewTimeZone));
      return;
    }

    const response = await api.updateProfileTimeZone({
      mode: 'manual',
      id: timeZoneId,
      displayName
    });
    if (response.ok === false) throw new Error(String(response.error || 'Time zone update failed'));
    const timeZone = (response as unknown as { timeZone?: UserTimeZone }).timeZone;
    if (!timeZone?.id) throw new Error('Time zone update did not return a profile');
    patchSelfProfile(profileTimeZonePatch(timeZone));
  }

  async function uploadTypingAnimation(file: File): Promise<string> {
    if (previewMode) {
      const previewUrl = URL.createObjectURL(file);
      patchSelfProfile({ custom_typing_animation_url: previewUrl, typing_animation: 'custom' });
      return previewUrl;
    }
    const res = await api.uploadTypingAnimation(file);
    if (res.ok === false) throw new Error(res.error || 'Typing animation upload failed');
    const url = String(res.url || (res.data as { url?: unknown } | undefined)?.url || '');
    if (!url) throw new Error('Typing animation upload did not return a URL');
    patchSelfProfile({ custom_typing_animation_url: url });
    return url;
  }

  async function setChatBackground(background: StoredChatBackground | null): Promise<void> {
    if (background) {
      await saveChatBackground(background);
      setChatBackgroundState(background);
      return;
    }
    await deleteChatBackground();
    setChatBackgroundState(null);
  }

  async function getCustomStatusHistory(): Promise<CustomStatusHistoryItem[]> {
    if (previewMode) {
      return [
        { id: 'preview-1', icon: '☕', text: 'Polishing mobile UI', createdAt: Date.now() },
        { id: 'preview-2', icon: '🎮', text: t('presence.gaming'), createdAt: Date.now() - 3600000 },
        { id: 'preview-3', icon: '📚', text: t('presence.studying'), createdAt: Date.now() - 7200000 }
      ];
    }
    const res = await api.getCustomStatusHistory();
    if (res.ok === false) throw new Error(res.error || 'Failed to load status history');
    return ((res.history || (res.data as { history?: CustomStatusHistoryItem[] } | undefined)?.history || []) as CustomStatusHistoryItem[]);
  }

  async function deleteCustomStatusHistory(id: number | string): Promise<void> {
    if (previewMode) return;
    const res = await api.deleteCustomStatusHistory(id);
    if (res.ok === false) throw new Error(res.error || 'Failed to delete status');
  }

  async function updateServerUrl(url: string): Promise<void> {
    const raw = String(url || '').trim();
    if (!raw) {
      await storageRef.current?.remove('serverUrl');
      const nextBaseUrl = runtimeApiBaseUrl(config.apiBaseUrl);
      setConfig((current) => ({ ...current, apiBaseUrl: nextBaseUrl }));
      setGameServiceConfig(buildFallbackGameServiceConfig(nextBaseUrl));
      return;
    }
    const normalized = normalizeBaseUrl(raw);
    await storageRef.current?.set('serverUrl', normalized);
    setConfig((current) => ({ ...current, apiBaseUrl: normalized }));
    setGameServiceConfig(buildFallbackGameServiceConfig(normalized));
  }

  async function exportKeyBackup(passphrase: string): Promise<Blob> {
    const localKeys = await ensureLocalKeypair();
    if (!localKeys.myPrivJwk) throw new Error('Missing private key');
    const encrypted = await exportPrivateKeyBackup(localKeys.myPrivJwk, passphrase);
    return new Blob([JSON.stringify(encrypted, null, 2)], { type: 'application/json' });
  }

  async function importKeyBackup(file: File, passphrase: string): Promise<void> {
    const encrypted = JSON.parse(await file.text()) as Record<string, unknown>;
    const privateKey = await importPrivateKeyBackup(encrypted, passphrase);
    const next = keypairFromPrivate(privateKey);
    keypairRef.current = next;
    setKeypair(next);
    await storageRef.current?.set('keypair', next);
    if (next.myPubJwk && token && !previewMode) {
      await api.profileUpdate({ pubkey_jwk: next.myPubJwk }).catch(() => undefined);
    }
  }

  async function restoreKeyBackup(id: string, password: string): Promise<void> {
    const passhash = await sha256Hex(password);
    const res = await api.keyRestoreByPassphrase({ id, passhash, passphrase: passhash });
    if (res.ok === false) throw new Error(res.error || 'Key restore failed');
    const encrypted = res.encryptedKey as Record<string, unknown> | string | undefined;
    if (!encrypted) throw new Error('No key backup found');
    const privateKey = await importPrivateKeyBackup(typeof encrypted === 'string' ? JSON.parse(encrypted) : encrypted, passhash);
    const expectedHash = String(res.private_key_hash || '');
    if (expectedHash) {
      const actualHash = await calculatePrivateKeyHash(privateKey);
      if (actualHash !== expectedHash) throw new Error('Key backup verification failed');
    }
    const next = keypairFromPrivate(privateKey);
    keypairRef.current = next;
    setKeypair(next);
    await storageRef.current?.set('keypair', next);
  }

  async function getDecryptedFileUrl(message: ChatMessage, peerId: string, options: MediaResolveOptions = {}): Promise<string> {
    const cacheKey = mediaCacheSlot(message, peerId);
    const signature = mediaCacheSignature(message, peerId, meRef.current?.id || '');
    const cached = mediaUrlCacheRef.current[cacheKey];
    if (!options.forceRefresh && cached?.signature === signature) return cached.url;
    if (cached) revokeCachedMediaUrl(cacheKey);

    const pendingKey = `${cacheKey}\u0000${signature}`;
    const pending = mediaLoadPromisesRef.current[pendingKey];
    if (pending) return pending;

    const loadPromise = (async () => {
      if (options.forceRefresh) {
        await deletePersistentMedia(cacheKey);
      } else {
        const persistentBlob = await getPersistentMedia(cacheKey, signature);
        if (persistentBlob) {
          const url = URL.createObjectURL(persistentBlob);
          mediaUrlCacheRef.current[cacheKey] = { url, signature };
          return url;
        }
      }

      if (isChatP2PFileMessage(message)) {
        throw new Error(t('p2pFileUnavailable'));
      }

      const fileUrl = message.file?.url || (message.file?.id ? `/file/${message.file.id}` : '');
      if (!fileUrl) throw new Error('Missing file URL');
      const absoluteUrl = assetUrl(fileUrl);
      const response = await fetch(absoluteUrl, { cache: options.forceRefresh ? 'reload' : 'default' });
      if (!response.ok) throw new Error(`File download failed (${response.status})`);
      const encryptedBlob = await response.blob();
      let plainBuffer: ArrayBuffer;
      let peer = peersRef.current.find((item) => item.id === peerId);
      if (!peer && !previewMode && token) {
        await refreshPeersRef.current().catch(() => undefined);
        peer = peersRef.current.find((item) => item.id === peerId);
      }
      if (message.iv && isGroup(peer)) {
        plainBuffer = await decryptArrayBufferFromGroup(peerId, message.iv, encryptedBlob);
      } else if (message.iv) {
        const currentMe = meRef.current;
        const otherId = message.fromId === currentMe?.id ? message.toId : message.fromId;
        const other = peersRef.current.find((item) => item.id === otherId) || peer;
        const currentKeypair = keypairRef.current;
        if (!other || !('pubkey_jwk' in other) || !other.pubkey_jwk || !currentKeypair.myPrivJwk) {
          throw new Error('Media keys are still loading');
        }
        plainBuffer = await decryptToArrayBuffer(currentKeypair.myPrivJwk, other.id, other.pubkey_jwk, message.iv, encryptedBlob);
      } else {
        plainBuffer = await encryptedBlob.arrayBuffer();
      }
      const mime = message.file?.mime || message.file?.type || 'application/octet-stream';
      if (!imageBufferLooksValid(plainBuffer, mime)) throw new Error('Decoded image data is invalid');
      const blob = new Blob([plainBuffer], { type: mime });
      const url = URL.createObjectURL(blob);
      mediaUrlCacheRef.current[cacheKey] = { url, signature };
      void savePersistentMedia(cacheKey, signature, blob);
      return url;
    })();

    mediaLoadPromisesRef.current[pendingKey] = loadPromise;
    try {
      return await loadPromise;
    } finally {
      if (mediaLoadPromisesRef.current[pendingKey] === loadPromise) {
        delete mediaLoadPromisesRef.current[pendingKey];
      }
    }
  }

  const accountGateReady = !token || previewMode || accountReady || (!!accountRefreshError && !accountRefreshing);
  const initialUiReady = previewMode || !token || (ready && cacheReady && accountGateReady && initialSyncReady);

  const value: SkyChatContextValue = {
    ready,
    initialUiReady,
    cacheReady,
    accountReady,
    accountRefreshing,
    accountRefreshError,
    previewMode,
    token,
    me,
    peers,
    chat,
    bots,
    typingByPeer,
    gameEventVersion,
    chatBackground,
    config,
    preferences,
    webPushState,
    t,
    api,
    login,
    register,
    logout,
    refreshPeers,
    openPeer,
    setPeerViewActive,
    loadOlderPeerHistory: loadOlderMessages,
    searchLocalMessages,
    revealLocalMessage,
    listLocalChatFiles,
    sendText,
    sendGif,
    invokeBot,
    sendFile,
    retryMessage,
    cancelChatP2PFile,
    editMessage,
    deleteMessage,
    clearPeer,
    sendFriendRequest,
    respondFriendRequest,
    markRead,
    markUnread,
    updateContactSettings,
    emitTyping,
    waitForSocketReady,
    emitSocketEvent,
    onSocketEvent,
    updateWeatherCity,
    updateWeatherVisibility,
    updatePreferences,
    enableWebPushNotifications,
    disableWebPushNotifications,
    refreshWebPushState,
    updateProfile,
    updateProfileTimeZone,
    uploadTypingAnimation,
    setChatBackground,
    getCustomStatusHistory,
    deleteCustomStatusHistory,
    updateServerUrl,
    exportKeyBackup,
    importKeyBackup,
    restoreKeyBackup,
    getDecryptedFileUrl,
    assetUrl
  };

  return <SkyChatContext.Provider value={value}>{children}</SkyChatContext.Provider>;
}

export function useSkyChat(): SkyChatContextValue {
  const value = useContext(SkyChatContext);
  if (!value) throw new Error('useSkyChat must be used inside AppProvider');
  return value;
}
